/**
 * THE PAPER SHELL, IN THE DOM.
 *
 * One semantic tree, `#game-ui`, laid exactly over the picture. Every surface on it is a
 * piece of the keeper's paper: the title is a ticket in the lower left, pause hangs from the
 * top edge, settings and save and credits are ledgers from the right, help is an open sheet
 * from the bottom. Each is opaque, torn on the side that faces the water, and casts a solid
 * shadow. Browser text sits on that paper and never on pixels; the legacy bottom bar is
 * retired, with its account and course action carried by these papers.
 *
 * WHAT THIS FILE OWNS: the tree, the copy, focus, and the keys a sheet answers. WHAT IT DOES
 * NOT: any decision about the sea. Everything that touches the voyage goes through
 * `GameCommands`, which the host (`world-entry.ts`) implements - and that interface has no
 * pause, stop or rate method, so a sheet cannot stop time (spec 13.1).
 *
 * FOCUS. Opening a sheet moves focus into it; Tab is trapped inside the top sheet and the
 * page behind it is `inert`; Escape or the sheet's own Back row pops exactly one frame and
 * returns focus to the control that opened that frame (`SheetFrame.returnFocusId`).
 *
 * NOTHING HERE IS INERT. An action that cannot be taken is not rendered: no Continue without
 * a valid save, no Save Recovery without a backup, no Skip Tips once the tips are past, no
 * music or dialogue slider because no such bus exists.
 *
 * THE CATCH INSTRUMENT (Task 5) is the one contextual surface: a stamped plate at the bottom
 * centre, torn on top, shown only while a catch resolves.
 * Its words come from `catchInstrumentAt` over the host's presentation; its Reel row is the
 * plate's Confirm and goes through `GameCommands.reel`, which is presentation only.
 */
import './game.css';
import { mountHarbourPanel, type HarbourPanelCommands } from './harbour-panel';
import portraitImage from './guide-portrait.png?url';
import type { GlyphFamily } from './actions';
import { catchInstrumentAt, type CatchInstrumentView } from './catch-instrument';
import { ART_CREDIT, BUILT_WITH, CODE, FITTINGS_CREDIT, MURRIN_CREDIT, OUTFITTER_ART_CREDIT, PAPER_CREDIT, PORTRAIT_CREDIT, REFERENCES, SHIPPED_ART, TORSK_CREDIT, WATER_CREDIT } from './credits';
import type { RecoverySummary, SaveProblem, StartResult } from './player-session';
import type { AppPresentation } from './presentation';
import { INITIAL_SHELL, reduceShell, topSheet, type SheetFrame, type SheetId, type ShellEvent, type ShellState, sheetOpen } from './shell';
import { advanceTutorial, replayTutorial } from './tutorial';
import { durationWords, type OfflineReturnView } from './offline-return';
import { FIRST_ATLAS_LETTER } from './season';
import { fishStudyView, STUDY_LETTERS } from './studies';
import { MURRIN_DESTINATIONS } from '../voyage/destinations';
import type { SailResult } from '../voyage/index';
import { plateAssetUrls, rootAssetUrls } from '../render/runtime-assets';
import { voyageGuidance } from './voyage-guidance';
import { DEFAULT_FISHER_IDENTITY, FISHER_NAME_MAX_LENGTH, FISHER_PORTRAIT_IDS, OUTFITTER_ITEMS, validateFisherIdentity, type FisherIdentity, type FisherPortraitId, type CosmeticSlot, type OutfitterView, type TransactionResult } from './outfitter';
import { FISHER_PORTRAIT_URLS, OUTFITTER_KEEPER_URL, OUTFITTER_PREVIEW_URLS } from './outfitter-assets';
import type { PlayerSettings, TutorialMode, TutorialStepId } from '../profile/schema';
import { AUTOSAVE_INTERVAL_MS, SPECIES, unlockedSpecies, type ImportResult } from '../sim/index';
import { PLATE_ASIDE_CX, PLATE_CENTRE_CX, PLATE_REST_Y, platePlacement } from '../render/fishing';

export interface GameCommands extends HarbourPanelCommands {
  /** A save that opens cleanly is on this browser. Continue Voyage is offered only then. */
  hasValidSave(): boolean;
  saveProblem(): SaveProblem | null;
  saveFailure(): string | null;
  recovery(): RecoverySummary;
  exportSave(): string;
  exportBackup(): string | null;
  importSave(text: string): ImportResult;
  restoreBackup(): ImportResult;
  /** Back up the current save, then replace it with a fresh run. */
  startNew(): StartResult;
  /** Start ticking the saved voyage. */
  continueVoyage(): StartResult;
  /** Start a fresh voyage. The host backs the old one up first when one exists. */
  setSail(confirmedReplacement?: boolean, identityDraft?: FisherIdentity): StartResult;
  identity(): FisherIdentity;
  updateIdentity(identity: FisherIdentity): TransactionResult;
  outfitter(): OutfitterView;
  buyCosmetic(id: string): TransactionResult;
  equipCosmetic(slot: CosmeticSlot, id: string | null): TransactionResult;
  /** Stop and save the voyage; the title comes back over the preview. */
  returnToTitle(): void;
  settings(): PlayerSettings;
  updateSettings(patch: Partial<PlayerSettings>): void;
  tutorial(): TutorialMode;
  setTutorial(mode: TutorialMode): void;
  skippedTips(): readonly TutorialStepId[];
  skipTip(): void;
  /** The pure read model of the running voyage, or null before one runs. */
  presentation(): AppPresentation | null;
  /** Which glyph family the hints show: the setting, or the last input used under `auto`. */
  glyphs(): GlyphFamily;
  /** The canvas ledger has Escape while it is open; the shell takes it otherwise. */
  ledgerOpen(): boolean;
  /**
   * Reel: advance the catch ceremony's presentation from the pull to the reveal. True when it
   * did. It cannot reach the sea: the same catch and the same reward arrive if nobody presses it.
   */
  reel(): boolean;
  setTarget(speciesId: string): void;
  sailTo(destinationId: string): SailResult;
  returnToNaturalCourse(): void;
  offlineReturn(): OfflineReturnView | null;
  dismissOfflineReturn(): void;
  finishAtlas(): void;
  continueFishing(): void;
  savedAt(): number | null;
  reload(): void;
}

/** The picture the plate stands against: the boat's centre down the frame and the art pixel, both in the host's frame. */
export interface PlateCamera {
  /** Art px from the frame's top: the rest row (270) in open water, lower while the arrival camera holds the boat. */
  readonly boatScreenY: number;
  /** One art px in CSS px (the host's `fit.apparent`), which turns the plate's DOM height into art px. */
  readonly artPx: number;
  /** The art viewport the canvas shows (CONTRACT 1.5ac); the authored 960x540 when absent. */
  readonly viewW?: number | undefined;
  readonly viewH?: number | undefined;
}

export interface GameUi {
  readonly root: HTMLElement;
  state(): ShellState;
  /** Reduce, render, and move focus for the transition. Returns the new state. */
  dispatch(event: ShellEvent): ShellState;
  /** Re-read the commands and redraw what changed (settings, tutorial, goal, save state). */
  refresh(): void;
  /**
   * Re-read the presentation and redraw the catch plate if its words changed. Cheap when
   * nothing did. The camera is the host's frame; it is read when a ceremony's plate first
   * shows, to choose its column, and kept for a refresh that brings none (Reel, a setting).
   */
  refreshCatch(camera?: PlateCamera): void;
  /** The plate's top-centre in CSS px from the root's top-left, or null while no plate is up. */
  catchPlateAnchor(): { readonly x: number; readonly y: number } | null;
  /** A startup, import or render failure: the recovery sheet with the reason in words. */
  showFatal(message: string): void;
  subscribe(listener: (state: ShellState, previous: ShellState) => void): () => void;
  destroy(): void;
}

// ---- small DOM helpers ------------------------------------------------------------------
type Attrs = Record<string, string | boolean | undefined>;
function h<K extends keyof HTMLElementTagNameMap>(tag: K, attrs: Attrs = {}, ...children: (Node | string | null | undefined)[]): HTMLElementTagNameMap[K] {
  const el = document.createElement(tag);
  for (const [k, v] of Object.entries(attrs)) {
    if (v === undefined || v === false) continue;
    if (k === 'class') el.className = String(v);
    else if (k === 'text') el.textContent = String(v);
    else el.setAttribute(k, v === true ? '' : v);
  }
  for (const c of children) if (c !== null && c !== undefined) el.append(c);
  return el;
}
function clear(el: HTMLElement): void { while (el.firstChild) el.removeChild(el.firstChild); }
function guidePortrait(): HTMLImageElement { return h('img', { class: 'portrait', src: portraitImage, alt: '' }); }
/** Domain save vocabulary stays stable; the paper uses familiar player words. */
function playerWords(text: string): string { return text.replaceAll('quarry study', 'study').replaceAll('quarry ground', 'fishing ground').replaceAll('quarry-ground', 'fishing-ground').replaceAll('paid minutes', 'counted minutes'); }
function progressionWords(p: AppPresentation): string { return p.player.xpToNextLevel > 0 ? `Level ${p.player.level} · ${p.player.xpToNextLevel} XP to level ${p.player.level + 1}` : `Level ${p.player.level} · highest level reached`; }
function canReturnToCourse(p: AppPresentation | null): boolean { return p?.voyage.chart.helm?.exit.needed === true; }

/** The key glyph a hint shows, by family. Keyboard and gamepad only: pointer needs no hint. */
const GLYPH: Readonly<Record<GlyphFamily, { back: string; confirm: string; reel: string | undefined }>> = {
  keyboard: { back: 'Esc', confirm: 'Enter', reel: 'Space' },
  // No gamepad hint for Reel until the pad is wired (Task 8): a hint for an input the build
  // does not read would be copy for a feature it cannot perform. Tap and click still reel.
  gamepad: { back: 'B', confirm: 'A', reel: 'LB' },
};

const murrinImage = rootAssetUrls['./baked/murrin-roost.png'];
function fishPlate(id: string, label: string): HTMLElement {
  return h('img', { class: 'fish-illustration', src: plateAssetUrls[`./baked/plates/${id}.png`], alt: label });
}
function voyageStatus(p: AppPresentation): string {
  const d = p.voyageDestination;
  if (d.status === 'moored') return `Moored at ${d.label ?? 'the ground'}. Fishing continues.`;
  if (d.etaSeconds !== null) return `Sailing · about ${durationWords(Math.ceil(d.etaSeconds) * 1000)} to arrival`;
  return 'Following the natural course. Fishing continues.';
}

/** A north-up local plot from the same position and ground coordinates as the voyage. */
function courseDiagram(p: AppPresentation): HTMLElement {
  const position = p.voyage.fix.position;
  const holder = h('div', { id: 'chart-course', class: 'course-diagram' });
  if (position === undefined) return holder;
  const points = [position, ...MURRIN_DESTINATIONS.map(d => d.position)];
  const minX = Math.min(...points.map(point => point.x)); const maxX = Math.max(...points.map(point => point.x));
  const minY = Math.min(...points.map(point => point.y)); const maxY = Math.max(...points.map(point => point.y));
  const project = (point: { x: number; y: number }): { x: number; y: number } => ({ x: 14 + (point.x - minX) / Math.max(1, maxX - minX) * 330, y: 12 + (maxY - point.y) / Math.max(1, maxY - minY) * 50 });
  const boat = project(position);
  const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
  svg.setAttribute('viewBox', '0 0 360 92'); svg.setAttribute('role', 'img'); svg.setAttribute('aria-label', 'Local course, north up. Square: your boat. Circles: the two Murrin grounds.');
  for (const d of MURRIN_DESTINATIONS) {
    const to = project(d.position); const current = p.voyageDestination.id === d.id;
    for (const kind of current ? ['route-outline', 'selected-route'] : ['possible-route']) {
      const line = document.createElementNS(svg.namespaceURI, 'line'); line.setAttribute('x1', String(boat.x)); line.setAttribute('y1', String(boat.y)); line.setAttribute('x2', String(to.x)); line.setAttribute('y2', String(to.y)); line.setAttribute('class', kind); svg.append(line);
    }
    const pin = document.createElementNS(svg.namespaceURI, 'circle'); pin.setAttribute('cx', String(to.x)); pin.setAttribute('cy', String(to.y)); pin.setAttribute('r', '5'); pin.setAttribute('class', 'ground-pin'); svg.append(pin);
    const label = document.createElementNS(svg.namespaceURI, 'text'); label.setAttribute('x', String(to.x)); label.setAttribute('y', String(to.y + 19)); label.setAttribute('text-anchor', to.x > 280 ? 'end' : 'start'); label.textContent = d.purpose === 'quarry' ? 'Nearshore' : 'Outer shelf'; svg.append(label);
  }
  const vessel = document.createElementNS(svg.namespaceURI, 'rect'); vessel.setAttribute('x', String(boat.x - 4)); vessel.setAttribute('y', String(boat.y - 4)); vessel.setAttribute('width', '8'); vessel.setAttribute('height', '8'); vessel.setAttribute('class', 'boat-pin'); svg.append(vessel);
  holder.append(svg, h('p', { class: 'mono', text: p.voyageDestination.id?.startsWith('murrin-') ? `Current route: ${p.voyageDestination.label ?? 'Murrin'}. Heavy line; square marks your boat.` : 'Natural course. Square marks your boat; both Murrin grounds are available.' }));
  return holder;
}

/**
 * THE TEAR. One hand-listed profile - along the edge in percent, depth in px - reused for
 * every torn side. The points are irregular on purpose: a tear generated at a fixed step
 * reads as a rendering bug (the owner's rejection ledger). The inner polygon is the same
 * profile stepped 2px into the sheet, which is what leaves the 2px deckle line.
 */
const TEAR: readonly (readonly [number, number])[] = [
  [0, 5], [3, 2], [7, 7], [12, 3], [16, 6], [21, 1], [27, 8], [31, 4], [36, 6], [41, 2],
  [45, 7], [52, 5], [56, 1], [61, 6], [67, 3], [72, 8], [78, 2], [83, 6], [88, 4], [93, 7], [97, 3], [100, 6],
];
type Side = 'top' | 'right' | 'bottom' | 'left';
function tearPolygon(sides: readonly Side[], inset: number): string {
  // The tear is part of the paper's footprint, so it is in grain pixels (`--reelin-g`), not
  // in the Large-paper unit: Large makes the rows taller, not the tear deeper.
  const u = (n: number): string => `calc(${n} * var(--reelin-g))`;
  const has = (s: Side): boolean => sides.includes(s);
  const pts: string[] = [];
  // Clockwise from the top-left corner. A torn side is the profile; a cut side is straight.
  if (has('top')) for (const [a, d] of TEAR) pts.push(`${a}% ${u(d + inset)}`);
  else pts.push(`0 0`, `100% 0`);
  if (has('right')) for (const [a, d] of TEAR) pts.push(`calc(100% - ${u(d + inset)}) ${a}%`);
  else pts.push(`100% 100%`);
  if (has('bottom')) for (const [a, d] of [...TEAR].reverse()) pts.push(`${a}% calc(100% - ${u(d + inset)})`);
  else pts.push(`0 100%`);
  if (has('left')) for (const [a, d] of [...TEAR].reverse()) pts.push(`${u(d + inset)} ${a}%`);
  return `polygon(${pts.join(', ')})`;
}
/**
 * THREE LAYERS MAKE ONE PIECE OF PAPER. The cast is the same torn outline in the darkened
 * sea, offset 6 grain px down and right: a solid block, the strip's own shadow idea. The
 * deckle is the outline in the deckle colour; the paper is the outline stepped 2px in, so
 * the deckle shows as a 2px line along the tear. The sheet element itself clips nothing -
 * a `filter: drop-shadow` on a clipped element is clipped with it, which is how the first
 * build shipped a shadow that never rendered (fix round 1).
 */
function dressSheet(sheet: HTMLElement, sides: readonly Side[]): HTMLElement {
  const cast = h('div', { class: 'cast', 'aria-hidden': 'true' });
  const deckle = h('div', { class: 'deckle', 'aria-hidden': 'true' });
  const paper = h('div', { class: 'paper' });
  cast.style.clipPath = tearPolygon(sides, 0);
  deckle.style.clipPath = tearPolygon(sides, 0);
  paper.style.clipPath = tearPolygon(sides, 2);
  sheet.append(cast, deckle, paper);
  return paper;
}
function tornSheet(id: string, cls: string, sides: readonly Side[], labelledBy: string): { sheet: HTMLElement; paper: HTMLElement } {
  const sheet = h('section', { id, class: `sheet ${cls}`, role: 'dialog', 'aria-modal': 'true', 'aria-labelledby': labelledBy, hidden: true });
  return { sheet, paper: dressSheet(sheet, sides) };
}

function row(id: string, label: string, opts: { hint?: string | undefined; stamp?: boolean } = {}): HTMLButtonElement {
  return h('button', { id, type: 'button', class: opts.stamp ? 'row stamp' : 'row' },
    h('span', { class: 'label', text: label }),
    opts.hint === undefined ? null : h('span', { class: 'hint', 'aria-hidden': 'true', text: opts.hint }));
}

/** The seven tutorial chapters, in the order tutorial.ts advances them. */
const CHAPTERS: readonly { readonly id: TutorialStepId; readonly title: string }[] = [
  { id: 'watch-autonomy', title: 'The boat works on its own' },
  { id: 'first-catch', title: 'The first catch' },
  { id: 'choose-quarry', title: 'Choose what to fish for' },
  { id: 'choose-ground', title: 'Choose where to sail' },
  { id: 'away-progress', title: 'Time away still counts' },
  { id: 'arrival', title: 'Making an arrival' },
  { id: 'next-course', title: 'The next course' },
];
type ChapterState = 'done' | 'now' | 'skipped' | 'unseen';
function chapterState(mode: TutorialMode, index: number): ChapterState {
  if (mode.kind === 'completed') return 'done';
  if (mode.kind === 'skipped') return 'skipped';
  if (mode.kind === 'active' || mode.kind === 'replay') {
    const at = CHAPTERS.findIndex((c) => c.id === mode.stepId);
    return index < at ? 'done' : index === at ? 'now' : 'unseen';
  }
  return 'unseen';
}
const STATE_WORD: Readonly<Record<ChapterState, string>> = { done: 'Done', now: 'Now', skipped: 'Skipped', unseen: 'Unseen' };
function tutorialLine(mode: TutorialMode): string {
  if (mode.kind === 'completed') return 'Tips: done.';
  if (mode.kind === 'skipped') return 'Tips: skipped. They stay in How Fishing Works.';
  if (mode.kind === 'active' || mode.kind === 'replay') {
    const at = CHAPTERS.findIndex((c) => c.id === mode.stepId) + 1;
    return `Tips: ${mode.kind === 'replay' ? 'replaying' : 'on'} chapter ${at} of ${CHAPTERS.length}.`;
  }
  return 'Tips: not seen yet. They begin when the voyage does.';
}
const TIPS_AHEAD = new Set<TutorialMode['kind']>(['unseen', 'offered', 'active', 'replay']);

function reasonWords(reason: string): string { return reason.replace(/[-_:]+/g, ' '); }
function importWords(result: ImportResult): string {
  if (result.ok) return result.notes.length === 0 ? 'Imported. The voyage is the pasted one now.' : `Imported, with notes: ${result.notes.map(reasonWords).join(', ')}.`;
  return `Not imported: ${reasonWords(result.reason)}. The current voyage is untouched.`;
}
function whenWords(ms: number | null): string {
  if (ms === null) return '';
  const d = new Date(ms);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')} ${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
}
function sourceWords(source: RecoverySummary['source']): string {
  return source === 'import' ? 'before an import' : source === 'new-voyage' ? 'before a new voyage' : 'taken by hand';
}

const FOCUSABLE = 'button, input, select, textarea, a[href], [tabindex]:not([tabindex="-1"])';
/** True when the platform will act on Enter itself, because focus is on a control that takes it. */
function activates(target: EventTarget | null): boolean {
  return target instanceof HTMLElement && target.matches('button, a[href], input, select, textarea, [contenteditable="true"]');
}
function focusables(scope: HTMLElement): HTMLElement[] {
  return Array.from(scope.querySelectorAll<HTMLElement>(FOCUSABLE)).filter((el) => {
    if (el.hidden || (el as HTMLButtonElement).disabled) return false;
    for (let p: HTMLElement | null = el.parentElement; p !== null && p !== scope; p = p.parentElement) if (p.hidden) return false;
    return true;
  });
}

export function mountGameUi(root: HTMLElement, commands: GameCommands): GameUi {
  /** Which kind of pointer pressed last (`onPointerType`), so `focusFirst` knows a tap from a click. */
  let lastPointerType = '';
  let state: ShellState = INITIAL_SHELL;
  const listeners = new Set<(s: ShellState, p: ShellState) => void>();
  // Transient, unsaved sheet state: confirmations shown in place, and status lines.
  let sailConfirm = false;
  let newConfirm = false;
  let titleStatus = '';
  let saveStatus = '';
  let fatalText = '';
  let chartPage: 'grounds' | 'quarry' | 'sea' = 'grounds';
  let guidePage: 'help' | 'collection' | 'studies' | 'voyage' | 'letters' = 'help';
  let studySpeciesId: string | null = null;
  let collectionSpeciesId: string | null = null;
  let collectionFutureOpen = false;
  let chartStatus = '';
  let fisherMode: 'new' | 'edit' = 'new';
  let fisherDraft: FisherIdentity = { ...DEFAULT_FISHER_IDENTITY };
  let fisherReplacementConfirmed = false;
  let fisherStatus = '';
  let fisherNeedsRecovery = false;
  let outfitterStatus = '';
  let outfitterNeedsRecovery = false;
  let outfitterSelected = OUTFITTER_ITEMS[0]!.id;

  // ---- the surfaces ----------------------------------------------------------------------
  const title = h('section', { id: 'title', class: 'sheet ticket', 'aria-labelledby': 'title-wordmark', hidden: true });
  const titlePaper = dressSheet(title, ['top', 'right']);
  const titleRows = h('div', { class: 'rows' });
  titlePaper.append(h('p', { id: 'title-wordmark', class: 'wordmark', text: 'Reelin' }), titleRows);
  const buildIdentity = `Reelin ${typeof __REELIN_VERSION__ === 'undefined' ? 'development' : __REELIN_VERSION__} · ${typeof __REELIN_BUILD__ === 'undefined' ? 'local' : __REELIN_BUILD__}`;
  titlePaper.append(h('p', { class: 'build-identity mono wash', text: `Version ${typeof __REELIN_VERSION__ === 'undefined' ? 'development' : __REELIN_VERSION__}` }));

  const slip = h('div', { id: 'ui-slip', class: 'sheet slip', hidden: true });
  const slipPaper = dressSheet(slip, ['left']);
  const menuBtn = row('ui-menu', 'Menu', { hint: GLYPH.keyboard.back });
  const voyageWords = h('div', { id: 'voyage-status' });
  slipPaper.append(voyageWords, menuBtn);
  const goalRibbon = h('section', { id: 'goal-ribbon', class: 'sheet goal-ribbon', 'aria-label': 'Current goal', hidden: true });
  const goalRibbonPaper = dressSheet(goalRibbon, ['right']);
  const goalLabel = h('p', { id: 'goal-label', class: 'instrument-title' });
  const goalProgress = h('p', { id: 'goal-progress', class: 'mono' });
  const goalReward = h('p', { id: 'goal-reward', class: 'wash' });
  const goalMarks = h('p', { id: 'goal-marks', class: 'mono' });
  const goalKicker = h('p', { class: 'mono instrument-kicker', text: 'The First Atlas' });
  const goalNext = row('goal-next', 'Choose a fish', { stamp: true });
  goalRibbonPaper.append(h('div', { class: 'goal-copy' }, goalKicker, goalLabel, goalProgress, goalMarks, goalReward), goalNext);
  const atlasReady = row('goal-ending', 'Read The First Atlas', { stamp: true }); atlasReady.hidden = true;
  goalRibbonPaper.append(atlasReady);
  const tabs = h('nav', { id: 'voyage-tabs', class: 'sheet voyage-tabs', 'aria-label': 'Voyage papers', hidden: true });
  const tabsPaper = dressSheet(tabs, ['top']);
  tabsPaper.append(row('ui-chart', 'Chart', { hint: 'C' }), row('ui-guide', 'Guide', { hint: 'G' }), row('ui-harbour', 'Harbour'));
  const tip = h('aside', { id: 'tutorial-tip', class: 'sheet tutorial-tip', 'aria-label': 'Harbourmaster tip', hidden: true });
  const tipPaper = dressSheet(tip, ['left']);
  // Keep guidance below the voyage slip, away from the catch plate's left-hand fallback.
  // Resize only, never the animation clock: large text follows the slip's real bottom edge.
  const goalResize = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(() => {
    if (!slip.hidden) root.style.setProperty('--reelin-slip-bottom', `${slip.offsetTop + slip.offsetHeight}px`);
  });
  goalResize?.observe(slip);

  const pause = tornSheet('sheet-pause', 'hanging', ['bottom'], 'pause-heading');
  const pauseRows = h('div', { class: 'rows' });
  pause.paper.append(
    h('h2', { id: 'pause-heading', class: 'sheet-title', text: 'Voyage menu' }),
    h('p', { class: 'wash note', text: 'Your crew keeps fishing while you use the menus.' }),
    pauseRows,
  );

  // The goal slip hangs under the menu slip on the right, torn on its left like the menu, so it
  // never lies across the pause sheet it was opened from. Task 6 folds it into the chart.
  const goal = tornSheet('sheet-chart', 'folded-chart', ['top', 'left'], 'goal-heading');
  const goalBody = h('div', { class: 'chart-body' });
  const chartNav = h('nav', { class: 'paper-tabs', 'aria-label': 'Chart pages' }, row('chart-grounds', 'Murrin grounds'), row('chart-quarry', 'Choose a fish'), row('chart-sea', 'Discovered sea'));
  goal.paper.append(h('h2', { id: 'goal-heading', class: 'sheet-title', text: 'The Murrin chart' }), chartNav, goalBody, h('div', { class: 'rows' }, row('goal-back', 'Back', { stamp: true })));

  const guide = tornSheet('sheet-guide', 'open-sheet', ['top'], 'guide-heading');
  const chapters = h('ul', { class: 'chapters', 'aria-label': 'Tutorial chapters' });
  const portrait = guidePortrait();
  const guideLine = h('p', { class: 'wash mono' });
  guide.paper.append(
    h('h2', { id: 'guide-heading', class: 'sheet-title', text: 'How fishing works' }),
    h('div', { class: 'columns' },
      h('div', { class: 'loop' }, portrait, h('div', {},
        h('p', { text: 'Watch the line: the crew casts, catches and sails automatically. Reel adds ceremony to the same catch.' }),
        h('p', { text: 'Choose a fish to begin its study. Keep one fish chosen for 30 counted minutes, and visit both Murrin grounds, to complete your first atlas.' }),
        h('p', { text: 'Each catch adds XP and coins. XP unlocks more fish; catches fill your guide and improve that fish’s mastery. Study counts time spent with one fish chosen.' }),
        h('p', { text: 'Choose a ground and the crew sails there. Arrival adds its mark automatically. Your next choice stays on the goal slip.' }),
        h('p', { text: 'After the First Atlas, study every fish for 30 counted minutes. Six, twelve and twenty-four completed studies bring letters from the Harbourmaster.' }),
        h('p', { text: 'Visit charted harbours for commissions, improvements and free pastimes. Spend earned coins on equipment to unlock advanced challenges, or on boat fittings at the Outfitter.' }),
        h('p', { text: 'Your plan works equally while away, for up to 24 hours. There is no need to keep this window open.' }),
      )),
      h('div', {}, h('h3', { class: 'section-title', text: 'Chapters' }), chapters, guideLine),
    ),
    h('div', { class: 'rows' }, row('guide-back', 'Back', { stamp: true })),
  );
  const guideHelp = guide.paper.querySelector('.columns') as HTMLElement;
  const guideContents = h('div', { id: 'guide-contents' });
  const guideNav = h('nav', { class: 'paper-tabs', 'aria-label': 'Field guide pages' }, row('guide-help', 'How fishing works'), row('guide-collection', 'Collection'), row('guide-studies', 'Studies'), row('guide-voyage', 'Voyage'), row('guide-letters', 'Letters'));
  guide.paper.insertBefore(guideNav, guideHelp);
  guide.paper.insertBefore(guideContents, guideHelp);

  const offline = tornSheet('sheet-offline', 'haul-receipt', ['bottom'], 'offline-heading');
  const offlineBody = h('div');
  offline.paper.append(h('h2', { id: 'offline-heading', class: 'sheet-title', text: 'While you were away' }), offlineBody,
    row('offline-chart', 'Choose the next ground'), row('offline-back', 'Keep fishing', { stamp: true }));
  const ending = tornSheet('sheet-ending', 'ending-letter', ['right', 'bottom'], 'ending-heading');
  const endingPortrait = guidePortrait();
  ending.paper.append(h('h2', { id: 'ending-heading', class: 'sheet-title', text: FIRST_ATLAS_LETTER.title }),
    h('div', { class: 'letter-copy' }, endingPortrait, h('p', { class: 'letter-body', text: FIRST_ATLAS_LETTER.body.replace('one quarry', 'one fish') }), h('p', { class: 'mono', text: FIRST_ATLAS_LETTER.signature })),
    h('div', { class: 'letter-actions' }, row('ending-credits', 'Credits and licenses'), row('ending-continue', 'Continue Fishing', { stamp: true })));

  const settings = tornSheet('sheet-settings', 'ledger', ['left'], 'settings-heading');
  const setMaster = h('input', { id: 'set-master', type: 'range', min: '0', max: '100', step: '1' });
  const setMasterOut = h('output', { for: 'set-master' });
  // The sea and effects buses exist (audio/engine.ts, Task 5), so their levels are the player's.
  const setSea = h('input', { id: 'set-sea', type: 'range', min: '0', max: '100', step: '1' });
  const setSeaOut = h('output', { for: 'set-sea' });
  const setEffects = h('input', { id: 'set-effects', type: 'range', min: '0', max: '100', step: '1' });
  const setEffectsOut = h('output', { for: 'set-effects' });
  const setMuted = h('input', { id: 'set-muted', type: 'checkbox' });
  const setMotion = h('input', { id: 'set-reduced-motion', type: 'checkbox' });
  // THE SYSTEM'S PREFERENCE IS A WORD OF ITS OWN, NOT A SEED. The profile's box adds to it and
  // never overrides it (fix round 2): the stylesheet's prefers-reduced-motion rule is
  // unconditional, and this line tells the player so while it applies.
  const motionQuery = ((): MediaQueryList | null => { try { return window.matchMedia('(prefers-reduced-motion: reduce)'); } catch { return null; } })();
  const motionNote = h('p', { id: 'set-motion-system', class: 'wash mono note', text: 'Your system asks for reduced motion, so sheets appear in place either way.' });
  motionNote.hidden = true;
  const setContrast = h('input', { id: 'set-contrast', type: 'checkbox' });
  const setScale1 = h('input', { id: 'set-ui-scale-1', type: 'radio', name: 'ui-scale', value: '1' });
  const setScale2 = h('input', { id: 'set-ui-scale-2', type: 'radio', name: 'ui-scale', value: '2' });
  const setText = h('select', { id: 'set-text-scale' }, h('option', { value: '1', text: 'Normal' }), h('option', { value: '1.25', text: 'Larger' }), h('option', { value: '1.5', text: 'Largest' }));
  const setReading = h('input', { id: 'set-reading', type: 'checkbox' });
  const setReveal = h('select', { id: 'set-catch-reveal' }, h('option', { value: 'all', text: 'Every catch' }), h('option', { value: 'notable', text: 'Records and new species' }), h('option', { value: 'new-only', text: 'New species only' }));
  const setRelaxed = h('input', { id: 'set-relaxed', type: 'checkbox' });
  const setGlyphs = h('select', { id: 'set-glyphs' }, h('option', { value: 'auto', text: 'Follow the last input used' }), h('option', { value: 'keyboard', text: 'Keyboard' }), h('option', { value: 'gamepad', text: 'Gamepad' }));
  const tipsLine = h('p', { class: 'wash mono note' });
  const line = (label: string, control: HTMLElement, why?: string, extra?: HTMLElement): HTMLElement =>
    h('div', { class: 'line' }, h('label', { for: control.id }, label, why === undefined ? null : h('span', { class: 'why', text: why })), control, extra ?? null);
  settings.paper.append(
    h('h2', { id: 'settings-heading', class: 'sheet-title', text: 'Settings' }),
    h('fieldset', { class: 'field' }, h('legend', { text: 'Sound' }),
      line('Master volume', setMaster, undefined, setMasterOut),
      line('Sea', setSea, 'The water under the boat.', setSeaOut),
      line('Effects', setEffects, 'Landings, records, the paper and the reel.', setEffectsOut),
      line('Mute', setMuted, 'S also mutes during a voyage.')),
    h('fieldset', { class: 'field' }, h('legend', { text: 'Motion and contrast' }),
      line('Reduced motion', setMotion, 'Sheets appear in place instead of sliding in from their edge. Off follows your system\'s own setting.'),
      motionNote,
      line('High contrast', setContrast, 'A heavier ink outline around the selected route and focused control.')),
    h('fieldset', { class: 'field' }, h('legend', { text: 'Size' }),
      h('div', { class: 'line' }, h('span', { class: 'label', text: 'Paper size' }),
        h('span', {}, h('label', { class: 'choice', for: 'set-ui-scale-1' }, setScale1, 'Normal'), h('label', { class: 'choice', for: 'set-ui-scale-2' }, setScale2, 'Large'))),
      line('Text size', setText)),
    h('fieldset', { class: 'field' }, h('legend', { text: 'Reading' }),
      line('Reading mode', setReading, 'The chart as a page of plain type instead of the picture. T toggles it during a voyage.')),
    h('fieldset', { class: 'field' }, h('legend', { text: 'Fishing' }),
      line('Catch reveal', setReveal, 'Which catches the sheet announces.'),
      line('Relaxed Fishing', setRelaxed, 'Hides the Reel prompt. The catch and its reward are the same either way.')),
    h('fieldset', { class: 'field' }, h('legend', { text: 'Input' }),
      line('Key glyphs', setGlyphs)),
    h('fieldset', { class: 'field' }, h('legend', { text: 'Tips' }),
      tipsLine,
      h('div', { class: 'line' }, h('span', { class: 'label', text: 'Replay the tips from the first chapter' }), h('button', { id: 'set-replay-tips', type: 'button', class: 'tool', text: 'Replay tips' })),
      h('div', { class: 'line' }, h('span', { class: 'label', text: 'Forget the tips so they are offered again' }), h('button', { id: 'set-reset-tips', type: 'button', class: 'tool', text: 'Reset tips' }))),
    h('div', { class: 'rows' }, row('settings-done', 'Done', { stamp: true })),
  );

  const credits = tornSheet('sheet-credits', 'ledger receipt', ['left'], 'credits-heading');
  const art = SHIPPED_ART.map((a) => `${a.count} ${a.said}`).join(', ');
  credits.paper.append(
    h('h2', { id: 'credits-heading', class: 'sheet-title', text: 'Credits and licenses' }),
    h('dl', {},
      h('dt', { text: 'Art' }), h('dd', {}, `${art}: used in Reelin. ${ART_CREDIT.what}. `, h('span', { class: 'figure', text: `${ART_CREDIT.who}. ${ART_CREDIT.licence}.` })),
      h('dt', { text: 'Water' }), h('dd', { text: `${WATER_CREDIT.what}. ${WATER_CREDIT.who}. ${WATER_CREDIT.licence}.` }),
      h('dt', { text: 'Murrin' }), h('dd', { text: `${MURRIN_CREDIT.what}. ${MURRIN_CREDIT.who}. ${MURRIN_CREDIT.licence}.` }),
      h('dt', { text: 'Torsk Vard' }), h('dd', { text: `${TORSK_CREDIT.what}. ${TORSK_CREDIT.who}. ${TORSK_CREDIT.licence}.` }),
      h('dt', { text: 'Harbourmaster' }), h('dd', { text: `${PORTRAIT_CREDIT.what}. ${PORTRAIT_CREDIT.who}. ${PORTRAIT_CREDIT.licence}.` }),
      h('dt', { text: 'Paper' }), h('dd', { text: `${PAPER_CREDIT.what}. ${PAPER_CREDIT.who}. ${PAPER_CREDIT.licence}.` }),
      h('dt', { text: 'Fishers and outfitter' }), h('dd', { text: `${OUTFITTER_ART_CREDIT.what}. ${OUTFITTER_ART_CREDIT.who}. ${OUTFITTER_ART_CREDIT.licence}.` }),
      h('dt', { text: 'Boat furnishings' }), h('dd', { text: `${FITTINGS_CREDIT.what}. ${FITTINGS_CREDIT.who}. ${FITTINGS_CREDIT.licence}.` }),
      h('dt', { text: 'Code' }), ...CODE.map((c) => h('dd', {}, h('span', { class: 'figure', text: `${c.what} ${c.version}` }), ` by ${c.who}. `, h('span', { class: 'figure', text: `${c.licence} licence.` }))),
      h('dt', { text: 'Built with' }), h('dd', { text: `${BUILT_WITH.map((b) => b.said).join(' and ')}. Neither ships in the game.` }),
      h('dt', { text: 'References studied; nothing from them ships' }),
      ...REFERENCES.map((r) => h('dd', {}, `${r.what}, ${r.who}. `, h('span', { class: 'figure', text: `${r.licence}.` }), ' ', h('span', { class: 'wash', text: r.ruling }))),
    ),
    h('div', { class: 'rows' }, row('credits-back', 'Back', { stamp: true })),
  );
  credits.paper.append(h('p', { class: 'mono wash', text: buildIdentity }));

  const save = tornSheet('sheet-save', 'ledger save', ['left'], 'save-heading');
  const fatalLine = h('p', { id: 'fatal-line', class: 'record fatal', hidden: true });
  const saveProblemLine = h('p', { id: 'save-problem', class: 'record', hidden: true });
  const saveText = h('textarea', { id: 'save-text', rows: '5', 'aria-label': 'Save text', spellcheck: 'false' });
  const saveStatusEl = h('p', { id: 'save-status', class: 'status', role: 'status', 'aria-live': 'polite' });
  const restoreRow = h('div', { class: 'line' });
  const newRows = h('div', { class: 'rows' });
  const saveFoot = h('div', { class: 'rows' });
  const savedAt = h('p', { id: 'save-autosave-status', class: 'mono' });
  const saveFile = h('input', { id: 'save-file', type: 'file', accept: '.txt,.json', 'aria-label': 'Import save file' });
  save.paper.append(
    h('h2', { id: 'save-heading', class: 'sheet-title', text: 'Save and recovery' }),
    fatalLine,
    saveProblemLine,
    h('h3', { class: 'section-title', text: 'Autosave' }),
    h('p', { class: 'wash', text: `The voyage saves itself every ${Math.round(AUTOSAVE_INTERVAL_MS / 1000)} seconds and on every choice, in this browser.` }),
    savedAt,
    h('p', { class: 'wash', text: 'Your save stays on this device. Reelin sends no save, account or gameplay data to a server. Export a copy before clearing browser data.' }),
    h('h3', { class: 'section-title', text: 'Export' }),
    h('div', { class: 'field' }, h('div', { class: 'line' }, h('span', { class: 'label', text: 'Put the whole save in the box below as text you can copy anywhere.' }), h('button', { id: 'save-export', type: 'button', class: 'tool', text: 'Export' }))),
    h('div', { class: 'paper-tabs' }, row('save-copy', 'Copy save'), row('save-download', 'Download save')),
    h('h3', { class: 'section-title', text: 'Import and backup' }),
    saveText,
    h('label', { class: 'save-file-label', for: 'save-file' }, 'Or choose a save file', saveFile),
    h('div', { class: 'field' },
      h('div', { class: 'line' }, h('span', { class: 'label', text: 'Replace the voyage with the text above. The current one is backed up first.' }), h('button', { id: 'save-import', type: 'button', class: 'tool', text: 'Import' })),
      restoreRow),
    saveStatusEl,
    newRows,
    saveFoot,
  );

  const fisher = tornSheet('sheet-fisher', 'ledger fisher-sheet', ['left'], 'fisher-heading');
  const fisherHeading = h('h2', { id: 'fisher-heading', class: 'sheet-title', text: 'Your fisher' });
  const fisherIntro = h('p', { class: 'wash fisher-intro' });
  const fisherName = h('input', { id: 'fisher-name', type: 'text', autocomplete: 'off', spellcheck: 'false', 'aria-describedby': 'fisher-name-hint' });
  const fisherPortraits = h('div', { class: 'fisher-portraits', role: 'group', 'aria-label': 'Choose a portrait' });
  const portraitNames: Readonly<Record<FisherPortraitId, string>> = { 'fisher-salt': 'Salt', 'fisher-reef': 'Reef', 'fisher-ember': 'Ember', 'fisher-moss': 'Moss' };
  for (const id of FISHER_PORTRAIT_IDS) fisherPortraits.append(h('button', { id: `portrait-${id}`, type: 'button', class: 'fisher-portrait-choice', 'data-portrait': id, 'aria-pressed': 'false', 'aria-label': `${portraitNames[id]} portrait` },
    h('img', { class: 'portrait', src: FISHER_PORTRAIT_URLS[id], alt: '' }), h('span', { text: portraitNames[id] })));
  const fisherStatusEl = h('p', { id: 'fisher-status', class: 'status', role: 'status', 'aria-live': 'polite' });
  const fisherRecovery = row('fisher-recovery', 'Open Save and Recovery'); fisherRecovery.hidden = true;
  const fisherSave = row('fisher-save', 'Set Sail', { stamp: true });
  fisher.paper.append(fisherHeading,
    h('div', { class: 'fisher-body' }, fisherIntro,
      h('label', { for: 'fisher-name', class: 'fisher-name-label', text: 'Name' }), fisherName,
      h('p', { id: 'fisher-name-hint', class: 'mono wash', text: `Up to ${FISHER_NAME_MAX_LENGTH} characters. You can change it later.` }),
      h('h3', { class: 'section-title', text: 'Choose your portrait' }), fisherPortraits,
      h('p', { class: 'wash', text: 'All four portraits are free.' }), fisherStatusEl, fisherRecovery),
    h('div', { class: 'fisher-footer' }, fisherSave, row('fisher-cancel', 'Cancel')));

  const outfitter = tornSheet('sheet-outfitter', 'open-sheet outfitter-sheet', ['top'], 'outfitter-heading');
  const outfitterWallet = h('p', { id: 'outfitter-wallet', class: 'mono outfitter-wallet' });
  const outfitIndex = h('nav', { class: 'outfitter-index', 'aria-label': 'Boat furnishings' });
  for (const item of OUTFITTER_ITEMS) outfitIndex.append(h('button', { id: `outfit-select-${item.id}`, type: 'button', class: 'outfitter-index-row', 'data-outfit-select': item.id, 'aria-pressed': 'false' },
    h('span', { text: item.label }), h('span', { class: 'mono wash', 'data-outfit-state': item.id })));
  const outfitPreview = h('img', { id: 'outfitter-preview', class: 'outfitter-preview', alt: '' });
  const outfitName = h('h3', { id: 'outfitter-item-name', class: 'instrument-title' });
  const outfitCost = h('p', { id: 'outfitter-cost', class: 'mono' });
  const outfitDescription = h('p', { id: 'outfitter-description', class: 'wash' });
  const outfitBuy = row('outfitter-buy', 'Buy', { stamp: true });
  const outfitEquip = row('outfitter-equip', 'Equip', { stamp: true });
  const outfitRemove = row('outfitter-remove', 'Remove from boat');
  const outfitStatus = h('p', { id: 'outfitter-status', class: 'status', role: 'status', 'aria-live': 'polite' });
  const outfitRecovery = row('outfitter-recovery', 'Open Save and Recovery'); outfitRecovery.hidden = true;
  outfitter.paper.append(
    h('div', { class: 'outfitter-header' }, h('img', { class: 'portrait', src: OUTFITTER_KEEPER_URL, alt: '' }),
      h('div', {}, h('h2', { id: 'outfitter-heading', class: 'sheet-title', text: 'Murrin Outfitters' }), h('p', { class: 'wash', text: 'Boat furnishings, bought with coins from your catches.' }), outfitterWallet)),
    h('div', { class: 'outfitter-body' }, outfitIndex,
      h('article', { class: 'outfitter-specimen' }, outfitPreview, h('div', {}, outfitName, outfitCost, outfitDescription, outfitBuy, outfitEquip, outfitRemove, outfitStatus, outfitRecovery))),
    h('div', { class: 'rows' }, row('outfitter-back', 'Back', { stamp: true })));

  // The catch instrument is its own paper. The legacy bottom strip and its attachment stem
  // have been retired; the same collision-aware placement still keeps the hull clear.
  const plate = h('section', { id: 'catch-plate', class: 'sheet plate', 'aria-label': 'Catch', hidden: true });
  const platePaper = dressSheet(plate, ['top']);

  const harbour = tornSheet('sheet-harbour', 'open-sheet harbour-sheet', ['top'], 'harbour-heading');
  const harbourHost = h('div', { class: 'harbour-register' });
  harbour.paper.append(harbourHost, h('div', { class: 'rows' }, row('harbour-back', 'Back', { stamp: true })));
  const harbourPanel = mountHarbourPanel(harbourHost, { commands,
    onChart: () => { chartPage = 'sea'; open('chart', 'ui-harbour'); },
    onResume: () => dispatch({ kind: 'resume' }), onSave: () => open('save', 'harbour-recovery'),
  });

  for (const s of [goal.sheet, guide.sheet, credits.sheet, offline.sheet, ending.sheet]) s.setAttribute('data-focus-heading', '');
  root.append(title, slip, goalRibbon, tabs, tip, plate, pause.sheet, goal.sheet, guide.sheet, settings.sheet, credits.sheet, save.sheet, offline.sheet, ending.sheet, fisher.sheet, outfitter.sheet, harbour.sheet);
  const sheetOf: Readonly<Record<SheetId, HTMLElement | null>> = {
    pause: pause.sheet, chart: goal.sheet, guide: guide.sheet, settings: settings.sheet, save: save.sheet, credits: credits.sheet,
    offline: offline.sheet, ending: ending.sheet, fisher: fisher.sheet, outfitter: outfitter.sheet, harbour: harbour.sheet,
  };

  // ---- rendering -------------------------------------------------------------------------
  function glyph(): (typeof GLYPH)[GlyphFamily] { return GLYPH[commands.glyphs()]; }

  function renderTitle(): void {
    clear(titleRows);
    const g = glyph();
    const hasSave = commands.hasValidSave();
    const problem = commands.saveProblem();
    titleRows.append(h('p', { class: 'wash ticket-loop', text: 'You choose fish and grounds. The crew fishes.' }));
    if (hasSave) titleRows.append(row('title-continue', 'Continue Voyage', { stamp: true, hint: g.confirm }));
    titleRows.append(row('title-sail', 'Set Sail', { stamp: !hasSave, hint: hasSave ? undefined : g.confirm }));
    if (sailConfirm) {
      titleRows.append(
        h('p', { id: 'title-sail-confirm', class: 'note', text: 'Start a new voyage? The current one is backed up first, and Save Recovery can bring it back.' }),
        row('title-sail-yes', 'Start a new voyage'),
        row('title-sail-no', 'Keep the current voyage'),
      );
    }
    titleRows.append(row('title-help', 'How Fishing Works'), row('title-settings', 'Settings'));
    if (hasSave) titleRows.append(row('title-save', 'Save and Recovery'));
    if (commands.recovery().available || commands.recovery().salvageAvailable || problem !== null) titleRows.append(row('title-recovery', 'Save Recovery'));
    if (problem !== null) titleRows.append(h('p', { id: 'title-save-problem', class: 'record note', text: 'The saved voyage could not be read. Save Recovery can export the original bytes. Starting a new voyage requires confirmation and preserves those bytes first.' }));
    titleRows.append(row('title-credits', 'Credits and Licenses'));
    const tutorial = commands.tutorial();
    if (TIPS_AHEAD.has(tutorial.kind)) titleRows.append(row('title-skip-tips', 'Skip Tips'));
    else if (tutorial.kind === 'skipped') titleRows.append(h('p', { id: 'title-tips-note', class: 'wash mono note', text: 'Tips skipped. They stay in How Fishing Works.' }));
    if (titleStatus !== '') titleRows.append(h('p', { id: 'title-status', class: 'record note', role: 'status', text: titleStatus }));
  }

  function renderPause(): void {
    clear(pauseRows);
    const g = glyph();
    const p = commands.presentation();
    pauseRows.append(
      row('pause-resume', 'Resume', { stamp: true, hint: g.back }),
      ...p === null ? [] : [h('p', { class: 'pause-purpose note', text: voyageGuidance(p).detail }), row('pause-next', voyageGuidance(p).action.label)],
      ...canReturnToCourse(p) ? [row('pause-natural', 'Let her drift')] : [],
      row('pause-goal', 'Current Goal'),
      row('pause-guide', 'Voyage Guide'),
      row('pause-fisher', 'Your Fisher'),
      row('pause-harbour', 'Harbour work & pastimes'),
      row('pause-outfitter', 'Outfitter'),
      row('pause-settings', 'Settings'),
      row('pause-save', 'Save and Recovery'),
      row('pause-title', 'Return to Title'),
    );
    if (TIPS_AHEAD.has(commands.tutorial().kind)) pauseRows.append(row('pause-skip-tips', 'Skip All Tips'));
  }

  function renderFisher(): void {
    fisherHeading.textContent = fisherMode === 'new' ? 'Meet your fisher' : 'Your fisher';
    fisherIntro.textContent = fisherMode === 'new' ? 'A name for the harbour register, and a face for your voyage.' : 'Your name and portrait travel with this save.';
    if (document.activeElement !== fisherName) fisherName.value = fisherDraft.name;
    fisherSave.querySelector('.label')!.textContent = fisherMode === 'new' ? 'Set Sail' : 'Save Fisher';
    fisherStatusEl.textContent = fisherStatus;
    fisherRecovery.hidden = !fisherNeedsRecovery;
    for (const button of fisherPortraits.querySelectorAll<HTMLButtonElement>('button')) button.setAttribute('aria-pressed', String(button.dataset['portrait'] === fisherDraft.portrait));
  }

  function renderOutfitter(): void {
    const view = commands.outfitter();
    outfitterWallet.textContent = `${view.balance} coins available`;
    const item = view.items.find(item => item.id === outfitterSelected) ?? view.items[0];
    if (item === undefined) return;
    outfitterSelected = item.id;
    for (const current of view.items) {
      outfitIndex.querySelector(`#outfit-select-${current.id}`)?.setAttribute('aria-pressed', String(current.id === item.id));
      const state = outfitIndex.querySelector(`[data-outfit-state="${current.id}"]`);
      if (state) state.textContent = current.equipped ? 'Equipped' : current.owned ? 'Owned' : `${current.cost} coins`;
    }
    outfitPreview.src = OUTFITTER_PREVIEW_URLS[item.id] ?? '';
    outfitPreview.alt = `${item.label} on your boat`;
    outfitName.textContent = item.label;
    outfitCost.textContent = item.equipped ? 'Owned · equipped' : item.owned ? 'Owned' : `${item.cost} coins`;
    outfitDescription.textContent = item.owned ? item.equipped ? 'Fitted to your boat. You can remove or swap it at any time.' : 'Yours to keep. Equip it when you want to use it.' : item.affordable ? 'Buy it once, then equip it from your collection here.' : `${item.cost - view.balance} more coins needed. Your crew earns coins with each catch.`;
    outfitBuy.querySelector('.label')!.textContent = `Buy for ${item.cost} coins`;
    outfitBuy.hidden = item.owned || !item.affordable;
    outfitEquip.hidden = !item.owned || item.equipped;
    outfitRemove.hidden = !item.equipped;
    outfitStatus.textContent = outfitterStatus;
    outfitRecovery.hidden = !outfitterNeedsRecovery;
  }

  function beginFisher(mode: 'new' | 'edit', from: string, confirmedReplacement = false): void {
    fisherMode = mode; fisherDraft = { ...(mode === 'new' ? DEFAULT_FISHER_IDENTITY : commands.identity()) };
    fisherReplacementConfirmed = confirmedReplacement; fisherStatus = ''; fisherNeedsRecovery = false; fisherName.value = fisherDraft.name;
    open('fisher', from);
  }

  function saveFisher(): void {
    fisherDraft = { ...fisherDraft, name: fisherName.value };
    if (!validateFisherIdentity(fisherDraft).ok) {
      fisherStatus = `Use a name of 1–${FISHER_NAME_MAX_LENGTH} characters, without control characters.`; renderFisher(); fisherName.focus(); return;
    }
    const result = fisherMode === 'new' ? commands.setSail(fisherReplacementConfirmed, fisherDraft) : commands.updateIdentity(fisherDraft);
    if (!result.ok) {
      fisherNeedsRecovery = /storage|recovery|save/.test(result.reason);
      fisherStatus = `Not saved: ${reasonWords(result.reason)}. Your choices are still here.`;
      renderFisher();
      return;
    }
    fisherNeedsRecovery = false;
    if (fisherMode === 'new') start(result);
    else { fisherDraft = { ...commands.identity() }; fisherName.value = fisherDraft.name; fisherStatus = 'Your fisher is saved.'; renderFisher(); }
  }

  function changeOutfit(action: 'buy' | 'equip' | 'remove'): void {
    const item = commands.outfitter().items.find(item => item.id === outfitterSelected);
    if (item === undefined) return;
    const result = action === 'buy' ? commands.buyCosmetic(item.id) : commands.equipCosmetic(item.slot, action === 'remove' ? null : item.id);
    outfitterNeedsRecovery = !result.ok && /storage|recovery|save/.test(result.reason);
    outfitterStatus = result.ok
      ? action === 'buy' ? `${item.label} purchased and saved. Choose Equip to fit it to your boat.` : action === 'equip' ? `${item.label} equipped and saved.` : `${item.label} removed from your boat and saved. You still own it.`
      : outfitterNeedsRecovery ? 'This change could not be saved. Your coins and equipment are unchanged.' : `Not changed: ${reasonWords(result.reason)}.`;
    renderOutfitter();
    if (result.ok) (action === 'equip' ? outfitRemove : outfitEquip).focus();
  }

  /** Reconcile this chart in place: a live ETA refresh must not detach a held button. */
  function updateChartNodes(current: Node, next: Node): void {
    if (current.nodeType === Node.TEXT_NODE) {
      if (current.nodeValue !== next.nodeValue) current.nodeValue = next.nodeValue;
      return;
    }
    if (current instanceof Element && next instanceof Element) {
      for (const attr of Array.from(current.attributes)) if (!next.hasAttribute(attr.name)) current.removeAttribute(attr.name);
      for (const attr of Array.from(next.attributes)) if (current.getAttribute(attr.name) !== attr.value) current.setAttribute(attr.name, attr.value);
    }
    let cursor = current.firstChild;
    for (const fresh of Array.from(next.childNodes)) {
      const key = fresh instanceof Element ? fresh.id : '';
      const candidate = key ? Array.from(current.childNodes).find(child => child instanceof Element && child.id === key) ?? null : cursor;
      const compatible = candidate !== null && candidate.nodeType === fresh.nodeType &&
        (!(candidate instanceof Element) || fresh instanceof Element && candidate.tagName === fresh.tagName && candidate.id === fresh.id && (key !== '' || candidate.getAttribute('class') === fresh.getAttribute('class')));
      let kept: Node;
      if (compatible) {
        kept = candidate!;
        if (kept !== cursor) current.insertBefore(kept, cursor);
        updateChartNodes(kept, fresh);
      } else {
        kept = fresh.cloneNode(true); current.insertBefore(kept, cursor);
      }
      cursor = kept.nextSibling;
    }
    while (cursor !== null) { const after = cursor.nextSibling; current.removeChild(cursor); cursor = after; }
  }

  function renderGoal(): void {
    const next = h('div', { class: 'chart-body' });
    fillGoal(next);
    updateChartNodes(goalBody, next);
  }

  function fillGoal(goalBody: HTMLElement): void {
    const p = commands.presentation();
    if (p === null) {
      goalBody.append(h('p', { class: 'wash', text: 'No voyage is running.' }));
      return;
    }
    chartNav.querySelector('#chart-grounds')?.setAttribute('aria-pressed', String(chartPage === 'grounds'));
    chartNav.querySelector('#chart-quarry')?.setAttribute('aria-pressed', String(chartPage === 'quarry'));
    chartNav.querySelector('#chart-sea')?.setAttribute('aria-pressed', String(chartPage === 'sea'));
    goal.sheet.querySelector('#goal-heading')!.textContent = chartPage === 'sea' ? 'The sea chart' : chartPage === 'quarry' ? 'Choose a fish' : 'The Murrin chart';
    if (chartPage === 'quarry') {
      goalBody.append(h('p', { class: 'chart-instruction', text: p.player.quarry.selected ? `Your crew is studying ${p.player.quarry.label}. Keeping one fish chosen for 30 counted minutes completes the study page.` : 'Choose a fish below to start its 30-minute study. Your crew will keep catching it while sailing or while you are away.' }), h('p', { class: 'wash', text: 'The catch pace, XP and coins stay the same whichever fish you choose.' }));
      const choices = h('div', { class: 'quarry-pages', 'aria-label': 'Unlocked fish' });
      for (const fish of unlockedSpecies(p.player.level)) {
        const selected = p.player.quarry.selected && fish.id === p.player.quarry.id;
        choices.append(h('button', { id: `quarry-${fish.id}`, type: 'button', class: 'quarry-plate', 'data-quarry': fish.id, 'aria-pressed': String(selected) },
          fishPlate(fish.id, ''), h('span', { class: 'instrument-title', text: fish.name }),
          h('span', { class: 'mono', text: selected ? 'Your study is counting' : `Choose ${fish.name} to study` }),
          h('span', { class: 'wash', text: `${Math.floor((p.player.mastery[fish.id] ?? 0) / 60)} minutes of mastery` })));
      }
      goalBody.append(choices);
    } else if (chartPage === 'sea') {
      // Keep destinations still under the pointer while live ETAs change.
      const places = [...p.voyage.chart.discovered].sort((a, b) => a.id.localeCompare(b.id));
      goalBody.append(h('p', { id: 'sea-chart-count', class: 'mono', text: `${places.length} / ${p.voyage.chart.chartedCount ?? places.length} sea places charted` }),
        h('p', { class: 'wash', text: 'The natural course reveals new islands. Choose any known place to sail back; fishing and your chosen study continue.' }));
      const index = h('div', { class: 'sea-place-index' });
      for (const place of places) {
        const current = p.voyageDestination.id === place.id;
        const eta = current && p.voyageDestination.etaSeconds !== null ? p.voyageDestination.etaSeconds : place.etaSeconds;
        index.append(h('button', { id: `sail-${place.id}`, type: 'button', class: 'sea-place-row', 'data-destination': place.id, 'aria-pressed': String(current) },
          h('span', { class: 'instrument-title', text: place.label }),
          h('span', { class: 'mono', text: place.isHere ? 'You are here' : `${current ? 'Current route · ' : ''}about ${durationWords(Math.ceil(eta) * 1000)} to arrival` }),
          h('span', { class: 'wash', text: place.isHere ? 'Fish here, or choose another destination.' : current ? 'The crew is already sailing here.' : 'Set this course →' })));
      }
      if (places.length === 0) index.append(h('p', { text: 'No distant places charted yet. Follow the natural course to bring more land into view.' }));
      goalBody.append(index);
      if (canReturnToCourse(p)) goalBody.append(row('chart-natural', 'Let her drift'), h('p', { class: 'wash', text: 'Rejoin the natural course to discover more of the sea.' }));
    } else {
      const map = h('div', { class: 'murrin-map' });
      map.append(h('p', { class: 'chart-instruction', text: 'Two grounds for your atlas. Choose a destination; the crew fishes and studies on the way.' }));
      map.append(h('div', { class: 'chart-world' }, h('img', { class: 'murrin-illustration', src: murrinImage, alt: 'Murrin: the sheltered quay and the outer rocky shelf' }), courseDiagram(p)));
      const grounds = h('div', { class: 'ground-plates' });
      for (const d of MURRIN_DESTINATIONS) {
        const v = p.localDestinations[d.id];
        grounds.append(h('button', { id: `sail-${d.id}`, type: 'button', class: `ground-plate ${v.current ? 'current' : ''}`, 'data-destination': d.id, 'aria-pressed': String(v.current) },
          h('span', { class: 'ground-mark', 'aria-hidden': 'true', text: d.purpose === 'quarry' ? '◉' : '⌖' }),
          h('span', { class: 'instrument-title', text: d.label }), h('span', { class: 'mono', text: v.label }),
          h('span', { text: d.purpose === 'quarry' ? 'Arrive for your Nearshore mark.' : 'Arrive for your outer shelf mark.' }), h('span', { class: 'ground-action mono', text: v.current ? p.phase === 'moored' ? 'You are here · arrival marked' : 'Current route →' : v.arrived ? 'Arrival marked · sail again' : 'Set this course →' })));
      }
      map.append(grounds);
      goalBody.append(map);
      if (canReturnToCourse(p)) goalBody.append(row('chart-natural', 'Let her drift'), h('p', { class: 'wash', text: 'Return to the natural course. Your chosen fish study continues.' }));
    }
    goalBody.append(h('p', { class: 'mono chart-goal', text: playerWords(`${p.goal.label}: ${p.goal.progress.toFixed(p.goal.units === 'paid minutes' ? 1 : 0)} / ${p.goal.target} ${p.goal.units}. ${p.goal.reward}`) }));
    if (chartStatus) goalBody.append(h('p', { id: 'chart-status', role: 'status', text: chartStatus }));
  }

  function renderGuide(): void {
    const collectionScroll = guideContents.querySelector('#collection-specimen') === null ? 0 : guideContents.scrollTop;
    const futurePages = guideContents.querySelector<HTMLDetailsElement>('.unreached-pages');
    if (futurePages !== null) collectionFutureOpen = futurePages.open;
    clear(chapters);
    const mode = commands.tutorial();
    CHAPTERS.forEach((c, i) => {
      const current = chapterState(mode, i);
      const s = current !== 'now' && commands.skippedTips().includes(c.id) ? 'skipped' : current;
      chapters.append(h('li', {}, h('button', { id: `replay-${c.id}`, type: 'button', class: 'chapter-replay', 'data-replay': c.id }, h('span', { class: 'n', text: String(i + 1) }), c.title), h('span', { class: `state ${s}`, text: STATE_WORD[s] })));
    });
    guideLine.textContent = tutorialLine(mode);
    guideHelp.hidden = guidePage !== 'help';
    guideContents.hidden = guidePage === 'help';
    clear(guideContents);
    guide.paper.querySelector('#guide-heading')!.textContent = guidePage === 'help' ? 'How fishing works' : 'The field guide';
    for (const button of guideNav.querySelectorAll('button')) button.setAttribute('aria-pressed', String(button.id === `guide-${guidePage}`));
    const p = commands.presentation();
    if (guidePage === 'help') return;
    if (p === null) { guideContents.append(h('p', { text: 'Set sail to begin your field guide. Your catches, marks and letters will be kept here.' })); return; }
    if (guidePage === 'collection') {
      const known = SPECIES.filter(fish => (p.player.catches[fish.id] ?? 0) > 0 || p.player.level >= fish.minLevel);
      const latestId = p.catchFacts !== null && (p.player.catches[p.catchFacts.speciesId] ?? 0) > 0 ? p.catchFacts.speciesId : null;
      const fish = known.find(fish => fish.id === collectionSpeciesId)
        ?? known.find(fish => fish.id === latestId)
        ?? known.find(fish => fish.id === p.player.quarry.id && (p.player.catches[fish.id] ?? 0) > 0)
        ?? known.find(fish => (p.player.catches[fish.id] ?? 0) > 0)
        ?? known[0]!;
      const caught = p.player.catches[fish.id] ?? 0;
      const found = known.filter(fish => (p.player.catches[fish.id] ?? 0) > 0).length;
      const specimen = h('article', { id: 'collection-specimen', class: 'collection-specimen' }, fishPlate(fish.id, ''),
        h('div', {}, h('p', { class: 'mono instrument-kicker', text: caught === 0 ? 'An open page' : fish.id === latestId ? 'Recent catch' : 'From your collection' }),
          h('h3', { class: 'instrument-title', text: fish.name }),
          h('p', { class: 'mono', text: caught > 0 ? `${caught} landed · record ${p.player.records[fish.id] ?? 0} g` : 'Unlocked · not yet landed' }),
          h('p', { class: 'mono wash', text: `${Math.floor((p.player.mastery[fish.id] ?? 0) / 60)} minutes of mastery` })));
      const index = h('nav', { class: 'collection-index', 'aria-label': 'Known fish pages' }, ...known.map(knownFish => {
        const count = p.player.catches[knownFish.id] ?? 0;
        return h('button', { id: `collection-${knownFish.id}`, type: 'button', class: 'collection-index-row', 'data-specimen': knownFish.id, 'aria-pressed': String(knownFish.id === fish.id) },
          h('span', { text: knownFish.name }), h('span', { class: 'mono wash', text: count > 0 ? `${count} landed` : 'Unlocked' }));
      }));
      const unreached = SPECIES.filter(fish => !known.includes(fish));
      const catalogue = h('div', { class: 'collection-catalogue' }, h('p', { class: 'mono collection-count', text: `${found} species landed · ${unlockedSpecies(p.player.level).length} unlocked` }), index);
      if (unreached.length) catalogue.append(h('details', { class: 'unreached-pages', open: collectionFutureOpen },
        h('summary', { id: 'collection-future', tabindex: '0', text: `${unreached.length} species still to unlock` }),
        h('p', { class: 'wash', text: `Your next new species unlocks at level ${unreached[0]!.minLevel}. Each catch adds XP toward it.` }),
        h('p', { class: 'mono', text: `Future pages open at levels ${unreached.map(fish => fish.minLevel).join(', ')}.` })));
      guideContents.append(h('div', { class: 'collection-spread' }, specimen, catalogue));
      guideContents.scrollTop = collectionScroll;
    } else if (guidePage === 'studies') {
      const view = fishStudyView(p.profile, p.player.level);
      const known = view.studies.filter(study => study.unlocked || study.complete);
      const study = known.find(study => study.id === studySpeciesId)
        ?? known.find(study => study.id === p.player.quarry.id && !study.complete)
        ?? known.find(study => study.id === view.next?.id) ?? known[0]!;
      const selected = p.player.quarry.selected && p.player.quarry.id === study.id;
      const nextLetter = STUDY_LETTERS.find(letter => letter.required > view.completed);
      guideContents.append(h('p', { id: 'study-progress', class: 'mono study-progress', text: `${view.completed} / ${view.total} fish studies complete${nextLetter ? ` · next letter at ${nextLetter.required}` : ' · all study letters earned'}` }));
      const specimen = h('article', { id: 'study-specimen', class: 'study-specimen' },
        h('div', { class: 'study-specimen-head' }, fishPlate(study.id, ''), h('div', {}, h('p', { class: 'mono instrument-kicker', text: study.complete ? 'A completed study' : selected ? 'Your current study' : 'A page to study' }),
          h('h3', { class: 'instrument-title', text: study.label }), h('p', { class: 'mono', text: `${study.minutes.toFixed(1)} / ${study.targetMinutes} counted minutes` }))));
      if (study.note !== null) specimen.append(h('p', { id: 'study-note', class: 'study-note', text: study.note }));
      else specimen.append(h('p', { class: 'wash', text: 'Keep this fish chosen for 30 counted minutes to complete its study and uncover the field note. Time away counts too.' }));
      if (selected) specimen.append(h('p', { class: 'mono', text: study.complete ? 'Your crew is fishing for this fish. Its study is complete.' : 'Chosen now. The crew is counting this study.' }));
      else if (study.unlocked) specimen.append(h('button', { id: `study-choose-${study.id}`, type: 'button', class: 'row stamp', 'data-quarry': study.id, 'data-quarry-label': study.label }, h('span', { class: 'label', text: `${study.complete ? 'Fish for' : 'Study'} ${study.label}` })));
      const index = h('nav', { class: 'collection-index', 'aria-label': 'Fish studies' }, ...known.map(fish => h('button', {
        id: `study-page-${fish.id}`, type: 'button', class: 'collection-index-row', 'data-study-page': fish.id, 'aria-pressed': String(fish.id === study.id),
      }, h('span', { text: fish.label }), h('span', { class: 'mono wash', text: fish.complete ? 'Studied' : `${fish.minutes.toFixed(1)} / ${fish.targetMinutes} min` }))));
      const locked = view.studies.filter(fish => !fish.unlocked && !fish.complete);
      const catalogue = h('div', {}, index);
      if (locked.length > 0) catalogue.append(h('p', { id: 'study-locked', class: 'wash', text: `${locked.length} more fish unlock as you gain XP. Next: ${locked[0]!.label}, at level ${locked[0]!.minLevel}.` }));
      guideContents.append(h('div', { class: 'collection-spread' }, specimen, catalogue));
    } else if (guidePage === 'voyage') {
      guideContents.append(h('h3', { class: 'section-title', text: 'Your progress' }),
        h('p', { id: 'guide-progression', class: 'mono', text: progressionWords(p) }),
        h('p', { id: 'guide-account', class: 'mono', text: `${p.player.xp} XP earned · ${p.player.coins} coins` }),
        h('p', { class: 'wash', text: `${unlockedSpecies(p.player.level).length} species unlocked. Each catch adds XP; records and mastery are in Collection.` }),
        h('h3', { class: 'section-title', text: 'Current goal' }), h('p', { text: playerWords(p.goal.label) }),
        h('p', { class: 'mono', text: `${p.goal.progress.toFixed(1)} / ${p.goal.target} ${p.goal.units === 'paid minutes' ? 'counted minutes' : p.goal.units}. ${p.goal.reward}` }),
        h('h3', { class: 'section-title', text: 'The First Atlas' }),
        h('ol', { class: 'goal-ladder' }, ...[
          ['quarry-study', 'Study one chosen fish for 30 counted minutes.'],
          ['murrin-nearshore', 'Arrive at Murrin Nearshore and mark the fishing ground.'],
          ['murrin-outer-shelf', 'Arrive at Murrin Outer Shelf and mark the survey.'],
          ['chart-room', 'Both arrival marks restore the chart-room automatically.'],
        ].map(([id, label]) => h('li', {}, h('span', { class: 'state', text: p.profile.milestones.includes(id!) ? 'Done' : 'Ahead' }), ` ${label}`))),
        h('p', { class: 'wash', text: 'These marks depend on counted study time and arrivals. A lucky catch is never required.' }));
      if (p.firstAtlas !== 'in-progress') guideContents.append(row('guide-ending', 'Read The First Atlas'));
    } else {
      guideContents.append(h('h3', { class: 'section-title', text: 'Letters from Murrin' }));
      if (p.profile.letters.includes(FIRST_ATLAS_LETTER.id) || p.firstAtlas !== 'in-progress') guideContents.append(h('p', { text: FIRST_ATLAS_LETTER.body.replace('one quarry', 'one fish') }), h('p', { class: 'mono', text: FIRST_ATLAS_LETTER.signature }), row('guide-ending', 'Read The First Atlas'));
      else guideContents.append(h('p', { text: 'The Harbourmaster is keeping a place for your first atlas. Study a chosen fish and bring home both Murrin arrival marks.' }));
      for (const letter of STUDY_LETTERS) if (p.profile.letters.includes(letter.id)) guideContents.append(h('article', { class: 'study-letter' }, h('h3', { class: 'section-title', text: letter.title }), h('p', { text: letter.body }), h('p', { class: 'mono', text: letter.signature })));
    }
  }

  let instrumentKey = '';
  let tipKey = '';
  function renderInstruments(): void {
    const p = commands.presentation();
    const visible = state.mode === 'voyage';
    goalRibbon.hidden = !visible || p === null; tabs.hidden = !visible; tip.hidden = true;
    if (!visible || p === null) return;
    atlasReady.hidden = p.firstAtlas !== 'ready';
    goalNext.hidden = p.firstAtlas === 'ready';
    const guidance = voyageGuidance(p);
    const key = JSON.stringify([guidance, p.voyageDestination, p.player.level, p.player.xp, p.player.coins, p.player.quarry, p.catchFacts, chartStatus]);
    if (key !== instrumentKey) {
    instrumentKey = key;
    goalKicker.textContent = p.firstAtlas === 'complete' || p.firstAtlas === 'continuing' ? 'The field guide' : 'The First Atlas';
    goalLabel.textContent = guidance.title;
    goalProgress.textContent = guidance.progress;
    goalMarks.textContent = guidance.marks;
    goalReward.textContent = guidance.detail;
    goalNext.querySelector('.label')!.textContent = guidance.action.label;
    clear(voyageWords);
    const currentGround = p.voyageDestination.id === 'murrin-nearshore' || p.voyageDestination.id === 'murrin-outer-shelf' ? p.localDestinations[p.voyageDestination.id] : null;
    const travelLine = p.phase === 'sailing' ? 'The crew is following your course; fishing continues.'
      : p.phase === 'returning' ? 'The crew is rejoining the natural course; fishing continues.'
      : p.phase === 'moored' ? `Arrived at ${p.voyageDestination.label ?? 'your destination'}. Fish here, or choose another course.` : '';
    const planLine = p.phase === 'moored' && currentGround?.arrived ? `Arrival marked: ${p.voyageDestination.label}. ${guidance.marks}.` : travelLine || chartStatus || (p.player.quarry.selected ? `Study: ${p.player.quarry.label} · time away counts too.` : 'No fish study chosen yet.');
    voyageWords.append(h('p', { class: 'mono instrument-kicker', text: 'Your crew' }),
      h('p', { id: 'crew-activity', class: 'instrument-title', text: guidance.crew }),
      h('p', { id: 'crew-plan', class: 'wash', text: planLine }),
      h('p', { id: 'crew-progression', class: 'mono crew-progression', text: progressionWords(p) }));
    if ((p.phase === 'sailing' || p.phase === 'returning') && p.voyageDestination.etaSeconds !== null) voyageWords.append(h('p', { class: 'mono', text: `${p.phase === 'returning' ? 'Rejoining' : 'Arrival'} in ${Math.max(1, Math.ceil(p.voyageDestination.etaSeconds))} seconds` }));
    if (p.catchFacts !== null) voyageWords.append(h('p', { id: 'crew-last-catch', class: 'mono catch-result', text: `Last catch: ${p.catchFacts.name} · +${p.catchFacts.xpDelta} XP · +${p.catchFacts.coinsDelta} coins` }));
    }
    const nextTipKey = JSON.stringify([commands.tutorial(), p.player.quarry.label, p.voyageDestination.label, p.phase]);
    if (nextTipKey === tipKey) { tip.hidden = tipPaper.childElementCount === 0; return; }
    tipKey = nextTipKey;
    const focusedTip = tip.contains(document.activeElement) ? (document.activeElement as HTMLElement).id : '';
    clear(tipPaper);
    const mode = commands.tutorial();
    slip.dataset.tips = mode.kind === 'active' || mode.kind === 'replay' || mode.kind === 'offered' ? 'on' : 'off';
    if (mode.kind !== 'active' && mode.kind !== 'replay' && mode.kind !== 'offered') return;
    const step = mode.kind === 'offered' ? 'watch-autonomy' : mode.stepId;
    const copy: Record<TutorialStepId, { line: string; action: string; id: string }> = {
      'watch-autonomy': { line: 'The boat sails and fishes on its own. Watch the line; your first catch is already on its way.', action: 'Watch the line', id: 'tip-watch' },
      'first-catch': { line: 'Watch the crew bring in a fish. Its XP unlocks more fish; the fish goes into your guide. Reel is optional.', action: 'Choose a fish', id: 'tip-quarry' },
      'choose-quarry': { line: `The crew caught ${p.player.quarry.label}. Now choose a fish: that starts the 30-minute study for your first atlas.`, action: 'Choose a fish', id: 'tip-quarry' },
      'choose-ground': { line: 'Open the Murrin chart and choose the sheltered Nearshore ground or the outer survey shelf.', action: 'Choose a ground', id: 'tip-chart' },
      'away-progress': { line: 'Your chosen plan works just as well while you are away. Up to 24 hours count; extra time is shown when you return.', action: 'Keep this plan', id: 'tip-away' },
      arrival: { line: p.phase === 'moored' ? `You have arrived at ${p.voyageDestination.label ?? 'Murrin'}. The mark is already in your guide.` : `The boat is following your plan to ${p.voyageDestination.label ?? 'Murrin'}. You can close this tip while it sails.`, action: 'See the chart', id: 'tip-chart' },
      'next-course': { line: 'The arrival mark is yours. Choose another ground, or return to the natural course. Fishing carries on.', action: 'Choose the next course', id: 'tip-chart' },
    };
    const current = copy[step]; const face = guidePortrait();
    tipPaper.append(h('div', { class: 'tip-copy' }, face, h('div', {}, h('p', { class: 'mono wash', text: mode.kind === 'replay' ? 'Harbourmaster · replay' : 'The Harbourmaster' }), h('p', { text: current.line }))),
      h('div', { class: 'tip-actions' }, row(current.id, current.action), h('div', { class: 'paper-tabs' }, row('tip-skip', 'Skip This Tip'), row('tip-skip-all', 'Skip All Tips'))));
    tip.hidden = false;
    if (focusedTip) document.getElementById(focusedTip)?.focus();
  }

  function renderOffline(): void {
    clear(offlineBody); const v = commands.offlineReturn();
    if (v === null) { offlineBody.append(h('p', { text: 'Your voyage is up to date. Fishing continues.' })); return; }
    offlineBody.append(h('p', { class: 'mono receipt-total', text: `${durationWords(v.paidMs)} counted · ${durationWords(v.skippedMs)} skipped` }), h('p', { text: v.plan }),
      h('p', { class: 'mono', text: `+${v.xpDelta} XP · +${v.coinDelta} coins` }),
      h('ul', { class: 'haul-catches' }, ...v.catchGroups.map(group => h('li', {}, `${group.label}`, h('span', { class: 'mono', text: `× ${group.count}` })))),
      ...v.newSpecies.map(name => h('p', { text: `New to the guide: ${name}.` })),
      ...v.records.map(record => h('p', { class: 'mono record', text: `${record.label}: new record ${record.afterG} g (was ${record.beforeG} g).` })),
      ...v.masteryMarks.map(name => h('p', { text: `${name} mastery completed.` })), ...v.arrivals.map(name => h('p', { text: `Arrival marked: ${name}.` })),
      ...v.stoppedBecause === null ? [] : [h('p', { class: 'record', text: v.stoppedBecause })], h('p', { text: v.recommendation }));
  }
  let courseKey = '';
  function refreshCourse(): void {
    if (goal.sheet.hidden || chartPage !== 'grounds') return;
    const p = commands.presentation(); if (p === null) return;
    const key = `${Math.floor(p.nowMs / 1000)}:${p.voyageDestination.id}:${p.phase}`;
    if (key === courseKey) return;
    courseKey = key;
    goalBody.querySelector('#chart-course')?.replaceWith(courseDiagram(p));
    const status = goalBody.querySelector('#chart-position'); if (status !== null) status.textContent = `Your position: ${voyageStatus(p)}`;
  }

  function renderSettings(): void {
    const s = commands.settings();
    setMaster.value = String(Math.round(s.masterVolume * 100));
    setMasterOut.value = `${Math.round(s.masterVolume * 100)}%`;
    setSea.value = String(Math.round(s.seaVolume * 100));
    setSeaOut.value = `${Math.round(s.seaVolume * 100)}%`;
    setEffects.value = String(Math.round(s.effectsVolume * 100));
    setEffectsOut.value = `${Math.round(s.effectsVolume * 100)}%`;
    setMuted.checked = s.muted;
    setMotion.checked = s.reducedMotion;
    motionNote.hidden = !(motionQuery?.matches ?? false);
    setContrast.checked = s.highContrastRoute;
    setScale1.checked = s.uiScale === 1;
    setScale2.checked = s.uiScale === 2;
    setText.value = String(s.textScale);
    setReading.checked = s.readingMode;
    setReveal.value = s.catchReveal;
    setRelaxed.checked = s.relaxedFishing;
    setGlyphs.value = s.glyphs;
    tipsLine.textContent = tutorialLine(commands.tutorial());
    applySettings(s);
  }

  function applySettings(s: PlayerSettings): void {
    const html = document.documentElement;
    html.style.setProperty('--reelin-ui-scale', String(s.uiScale));
    html.style.setProperty('--reelin-text-scale', String(s.textScale));
    // Only ever `reduced`, never `full`: the profile's choice removes the arrivals on top of
    // the system's own rule, which the stylesheet applies whatever this attribute says. A
    // profile carrying the schema default is not a choice against the operating system.
    if (s.reducedMotion) html.setAttribute('data-reelin-motion', 'reduced'); else html.removeAttribute('data-reelin-motion');
    if (s.highContrastRoute) html.setAttribute('data-reelin-contrast', 'on'); else html.removeAttribute('data-reelin-contrast');
  }

  function renderSave(): void {
    const fatal = state.mode === 'fatal-recovery';
    fatalLine.hidden = !fatal;
    fatalLine.textContent = fatal ? `${fatalText} Export the save below to keep it, or reload the page to try again.` : '';
    clear(restoreRow);
    const r = commands.recovery();
    const problem = commands.saveProblem();
    saveProblemLine.hidden = problem === null;
    saveProblemLine.textContent = problem?.kind === 'invalid' ? 'The saved voyage could not be read. Export preserves its exact original bytes for copying; Download save writes them to a file. A confirmed new voyage preserves those bytes in recovery first.' : problem?.kind === 'unreadable' ? 'This browser did not allow its saved data to be read. Export can preserve the voyage currently in memory. Reload after restoring storage access.' : '';
    const stamp = commands.savedAt();
    savedAt.textContent = `${commands.saveFailure() === null ? '' : 'Changes could not be saved. Export a copy before closing. '}${stamp === null ? 'No voyage saved yet.' : `Last saved on this device: ${whenWords(stamp)}.`}`;
    if (r.available) {
      restoreRow.append(
        h('span', { class: 'label' }, `Bring back the backup taken ${sourceWords(r.source)}`, r.timestamp === null ? null : h('span', { class: 'why', text: whenWords(r.timestamp) })),
        h('button', { id: 'save-restore', type: 'button', class: 'tool', text: 'Restore' }),
      );
    } else if (r.note !== '') {
      restoreRow.append(h('span', { class: 'label wash', text: `No backup can be read: ${reasonWords(r.note)}.` }));
    } else {
      restoreRow.append(h('span', { class: 'label wash', text: 'No backup yet. One is taken before every import and every new voyage.' }));
    }
    if (r.available || r.salvageAvailable) restoreRow.append(h('button', { id: 'save-export-backup', type: 'button', class: 'tool', text: 'Export backup bytes' }), h('button', { id: 'save-download-backup', type: 'button', class: 'tool', text: 'Download backup bytes' }));
    saveStatusEl.textContent = saveStatus;
    clear(newRows);
    if (!fatal) {
      newRows.append(row('save-new', 'New voyage'));
      if (newConfirm) {
        newRows.append(
          h('p', { id: 'save-new-confirm', class: 'note', text: 'Start a new voyage? The current one is backed up first and can be restored from this sheet.' }),
          row('save-new-yes', 'Start a new voyage', { stamp: true }),
          row('save-new-no', 'Keep the current voyage'),
        );
      }
    }
    clear(saveFoot);
    if (fatal) saveFoot.append(row('save-reload', 'Reload the page', { stamp: true }));
    else saveFoot.append(row('save-back', 'Back', { stamp: !newConfirm, hint: glyph().back }));
  }

  // ---- the catch instrument ------------------------------------------------------------
  let plateKey = '';
  let plateCamera: PlateCamera = { boatScreenY: PLATE_REST_Y, artPx: 1 };
  /** The column chosen for the ceremony under way, by its event, so a plate never moves mid-ceremony. */
  let plateHome: { readonly atMs: number; readonly cx: number; readonly home: 'centre' | 'aside' } | null = null;
  let placementKey = '';
  let measuredPlateHeight = 0;
  function catchView(): { readonly view: CatchInstrumentView; readonly presentation: AppPresentation } | null {
    if (state.mode !== 'voyage') return null;
    const p = commands.presentation();
    if (p === null) return null;
    const v = catchInstrumentAt(p.catch, p.catchFacts, p.profile.settings);
    return v.mode === 'hidden' ? null : { view: v, presentation: p };
  }
  /** The plate's words for one form of the instrument. */
  function fillPlate(v: CatchInstrumentView, hint: string | undefined): void {
    plate.classList.remove('prompt', 'compact', 'full');
    clear(platePaper);
    plate.classList.add(v.mode);
    const lines = h('div', { class: 'plate-lines' }, ...v.detail.map((t) => h('p', { class: 'mono', text: playerWords(t) })));
    if (v.mode === 'prompt') {
      // The plate in its first state: the line pulls, and one stamped row is the plate's Confirm.
      platePaper.append(
        h('p', { class: 'plate-title', text: v.heading }),
        h('div', { class: 'rows' }, row('catch-reel', 'Reel', { stamp: true, hint })),
        h('p', { class: 'wash mono plate-note', text: v.detail[0] ?? '' }),
      );
      return;
    }
    if (v.mode === 'compact') {
      platePaper.append(h('p', { class: 'plate-title', text: v.heading }), lines);
      return;
    }
    // A field-guide plate: the animal beside its name, the stamp at the right, the lines below.
    platePaper.append(
      h('div', { class: 'plate-head' },
        v.speciesId === null ? null : h('div', { class: 'plate-specimen' }, fishPlate(v.speciesId, '')),
        h('h2', { class: 'plate-title', text: v.heading }),
        v.stamp === null ? null : h('span', { class: `plate-stamp ${v.stamp.tone}`, text: v.stamp.word })),
      h('div', { class: 'plate-body' }, lines),
      h('p', { class: 'wash mono plate-next', text: commands.presentation()?.player.quarry.selected === false ? 'Choose a fish to begin its 30-minute study.' : playerWords(v.next) }),
    );
  }
  /**
   * WHICH COLUMN THE CEREMONY STANDS IN, chosen once per event when its plate first shows.
   * The design brief's home is the bottom centre; the hull decides whether the plate fits
   * there (render/fishing.ts `platePlacement`). The height that matters is the tallest form
   * the ceremony will show, so the reveal's plate is built and measured now, while the prompt
   * is what is about to be shown: the plate is laid out for the measurement but no frame is
   * painted between it and the words that follow, so nothing flashes. Measured in CSS px off
   * the DOM and turned into art px by the host's art pixel, so Large paper, a text scale and
   * a long name all count.
   */
  function placePlate(v: CatchInstrumentView, p: AppPresentation, hint: string | undefined): void {
    const atMs = p.catch.event?.atMs;
    if (atMs === undefined) return;
    const sizeKey = JSON.stringify([atMs, plateCamera.artPx, plateCamera.viewW, plateCamera.viewH, p.profile.settings.uiScale, p.profile.settings.textScale]);
    if (placementKey !== sizeKey) {
      placementKey = sizeKey;
      const reveal = catchInstrumentAt({ ...p.catch, phase: 'reveal' }, p.catchFacts, p.profile.settings);
      measuredPlateHeight = 0;
      for (const form of [v, reveal]) {
        if (form.mode === 'hidden') continue;
        fillPlate(form, hint);
        measuredPlateHeight = Math.max(measuredPlateHeight, plate.offsetHeight);
      }
      fillPlate(v, hint);
    }
    const placed = platePlacement({ boatScreenY: plateCamera.boatScreenY, plateHeightPx: measuredPlateHeight / plateCamera.artPx, viewW: plateCamera.viewW, viewH: plateCamera.viewH });
    let { cx, home } = placed;
    // A moving arrival camera can make centre unsafe. Step aside once; never jump back.
    // The aside column of the CURRENT view, so a resize mid-ceremony keeps the plate beside the hull.
    if (plateHome?.atMs === atMs && plateHome.home === 'aside' && home !== 'aside') { cx = Math.floor((plateCamera.viewW ?? 960) / 2) - (PLATE_CENTRE_CX - PLATE_ASIDE_CX); home = 'aside'; }
    plateHome = { atMs, cx, home };
    document.documentElement.style.setProperty('--reelin-plate-cx', String(cx));
  }
  function renderCatch(): void {
    const c = catchView();
    const hint = glyph().reel;
    // The event's instant is in the key: two catches with the same words are still two plates.
    const key = c === null ? '' : JSON.stringify([c.presentation.catch.event?.atMs, c.view.mode, c.view.speciesId, c.view.heading, c.view.stamp, c.view.detail, c.view.next, c.presentation.player.quarry.selected, hint ?? '']);
    if (key === plateKey) { if (c !== null) placePlate(c.view, c.presentation, hint); return; }
    plateKey = key;
    plate.hidden = c === null;
    plate.classList.remove('prompt', 'compact', 'full');
    clear(platePaper);
    if (c === null) return;
    placePlate(c.view, c.presentation, hint);
    fillPlate(c.view, hint);
  }

  function render(): void {
    const top = topSheet(state);
    const modal = sheetOpen(state);
    root.hidden = state.mode === 'boot';
    title.hidden = state.mode !== 'title';
    slip.hidden = state.mode !== 'voyage';
    for (const [id, el] of Object.entries(sheetOf) as [SheetId, HTMLElement | null][]) {
      if (el === null) continue;
      const open = state.mode === 'fatal-recovery' ? id === 'save' : top?.id === id;
      el.hidden = !open;
      el.toggleAttribute('inert', open && (state.mode === 'fatal-recovery' ? false : top?.id !== id));
    }
    title.toggleAttribute('inert', modal && !title.hidden);
    slip.toggleAttribute('inert', modal && !slip.hidden);
    for (const id of ['sea', 'reach', 'strip', 'devtoggle']) document.getElementById(id)?.toggleAttribute('inert', modal);
    // Rows are rebuilt, so the control under focus is found again by id afterwards.
    const focused = (document.activeElement as HTMLElement | null)?.id ?? '';
    if (!title.hidden) renderTitle();
    if (!pause.sheet.hidden) renderPause();
    if (!goal.sheet.hidden) renderGoal();
    if (!guide.sheet.hidden) renderGuide();
    if (!fisher.sheet.hidden) renderFisher();
    if (!outfitter.sheet.hidden) renderOutfitter();
    if (!harbour.sheet.hidden) harbourPanel.update();
    renderSettings();
    if (!save.sheet.hidden) renderSave();
    if (!offline.sheet.hidden) renderOffline();
    renderInstruments();
    renderCatch();
    if (focused !== '' && document.activeElement !== document.getElementById(focused)) document.getElementById(focused)?.focus({ preventScroll: true });
  }

  // ---- focus -----------------------------------------------------------------------------
  /**
   * Focus enters the sheet. A sheet whose first control is at its foot (help, credits, the
   * goal slip, and the recovery sheet with its one red sentence at the top) takes focus on its
   * heading, so the paper is not scrolled to the foot before it has been read; a sheet that
   * is a list of actions takes focus on the first action.
   */
  function focusFirst(scope: HTMLElement): void {
    const heading = scope.querySelector<HTMLElement>('[data-focus-heading] .sheet-title');
    if (heading !== null) { heading.tabIndex = -1; heading.focus(); return; }
    const first = focusables(scope)[0];
    // A TAP DOES NOT SUMMON A KEYBOARD NOBODY ASKED FOR. Measured on the iPad build: Set Sail
    // opened the fisher sheet, focus went to the name field, the on-screen keyboard came up over
    // half the sheet and the next tap aimed at Set Sail typed a letter into the name. After a touch
    // or pen, a sheet whose first control is a text field takes focus on its title instead; the
    // field is one tap away. Keyboard and mouse players keep the field focused, as before.
    if (first !== undefined && (lastPointerType === 'touch' || lastPointerType === 'pen') && first.matches('input:not([type="checkbox"], [type="radio"], [type="range"], [type="button"], [type="file"]), textarea')) {
      const title = scope.querySelector<HTMLElement>('.sheet-title');
      if (title !== null) { title.tabIndex = -1; title.focus({ preventScroll: true }); return; }
    }
    if (first !== undefined) first.focus();
  }
  function focusId(id: string, fallback: HTMLElement): void {
    const el = document.getElementById(id);
    if (el !== null && !el.hidden && el.closest('[hidden]') === null) el.focus();
    else focusFirst(fallback);
  }
  function focusForTransition(previous: ShellState, popped: SheetFrame | null): void {
    const top = topSheet(state);
    if (state.mode === 'fatal-recovery') { save.sheet.setAttribute('data-focus-heading', ''); focusFirst(save.sheet); return; }
    // A sheet on top, over the picture or over the page of type.
    if (top !== null) {
      const el = sheetOf[top.id];
      if (popped !== null) focusId(popped.returnFocusId, el ?? root);
      else if (el !== null && previous.sheets.length < state.sheets.length) focusFirst(el);
      return;
    }
    if (state.mode === 'reading') {
      // The last frame over the page of type was popped: back to the control on the page
      // that opened it. The page itself is the host's; its first control is the fallback.
      if (popped !== null) focusId(popped.returnFocusId, document.body);
      return;
    }
    if (state.mode === 'title') {
      // Not at boot: the page's own tab order reaches the ticket first, and a load that grabs
      // focus is a load that scrolls a reader away from wherever they were.
      if (popped !== null) focusId(popped.returnFocusId, title); else if (previous.mode !== 'title' && previous.mode !== 'boot') focusFirst(title);
      return;
    }
    if (state.mode === 'voyage') {
      if (popped !== null) { focusId(popped.returnFocusId, slip); return; }
      // Focus follows the player from the ticket to the PICTURE - but only when the player is
      // the one who started the voyage. `?dev` starts it from the script at boot with nothing
      // focused, and a page that grabs focus at load rearranges the tab order the platform
      // instruments measure (a11y-proof.mjs). It lands on the sea and not on the menu slip
      // because Space is Reel (Task 5): parked on a button, the first press would be that
      // button's own activation and the menu would open in place of the reel.
      const at = document.activeElement;
      if ((previous.mode === 'title' || previous.mode === 'sheet') && at !== null && at !== document.body) {
        const sea = document.getElementById('sea');
        if (sea !== null) { sea.tabIndex = -1; sea.focus(); } else menuBtn.focus();
      }
    }
  }

  function dispatch(event: ShellEvent): ShellState {
    const previous = state;
    let next = reduceShell(state, event);
    if (event.kind === 'booted' && commands.saveProblem()?.kind === 'unreadable') {
      fatalText = 'This browser did not allow saved data to be read.';
      next = reduceShell(next, { kind: 'fatal' });
    }
    if (next === previous) return state;
    const popped = event.kind === 'back' && previous.sheets.length > next.sheets.length ? previous.sheets[previous.sheets.length - 1] ?? null : null;
    if (event.kind === 'resume' || event.kind === 'return-to-title' || event.kind === 'start-voyage') { sailConfirm = false; newConfirm = false; }
    if (event.kind === 'back' && popped?.id === 'save') { newConfirm = false; saveStatus = ''; }
    state = next;
    render();
    // THE HOST HEARS IT FIRST, THEN FOCUS MOVES. The host's listener shows and hides its own
    // surfaces (the plain-type page, the reach nav) and moves focus with them; if it ran
    // after the sheet took focus, it would take that focus away (fix round 1). A listener
    // that dispatches again has already focused for the newer state, so this one stands down.
    for (const l of listeners) l(state, previous);
    if (state !== next) return state;
    // Resume drops every frame; focus goes where the bottom frame came from.
    focusForTransition(previous, popped ?? (event.kind === 'resume' ? previous.sheets[0] ?? null : null));
    return state;
  }

  function open(id: SheetId, from: string): void { dispatch({ kind: 'open-sheet', id, returnFocusId: from }); }
  function start(result: StartResult): void {
    if (result.ok) {
      chartStatus = '';
      const tutorial = commands.tutorial();
      if (tutorial.kind === 'unseen' || tutorial.kind === 'offered') commands.setTutorial({ kind: 'active', stepId: 'watch-autonomy' });
      titleStatus = ''; dispatch({ kind: 'start-voyage' });
      if (commands.offlineReturn() !== null) open('offline', 'ui-chart');
    }
    else {
      titleStatus = `Could not start: ${reasonWords(result.reason)}.`;
      if (result.reason === 'storage-unwritable' || result.reason === 'storage-unreadable' || commands.saveFailure() !== null) { fatalText = 'The voyage could not be saved on this device.'; dispatch({ kind: 'fatal' }); }
      else render();
    }
  }

  // ---- wiring ----------------------------------------------------------------------------
  root.addEventListener('click', (e) => {
    const btn = (e.target as HTMLElement | null)?.closest<HTMLElement>('button');
    if (btn === null || btn === undefined || !root.contains(btn)) return;
    if (btn.dataset['studyPage']) { studySpeciesId = btn.dataset['studyPage']; renderGuide(); document.getElementById(btn.id)?.focus({ preventScroll: true }); return; }
    if (btn.dataset['specimen']) {
      collectionSpeciesId = btn.dataset['specimen']; renderGuide(); document.getElementById(btn.id)?.focus(); return;
    }
    if (btn.dataset['portrait']) {
      fisherDraft = { ...fisherDraft, portrait: btn.dataset['portrait'] as FisherPortraitId }; fisherStatus = ''; renderFisher(); return;
    }
    if (btn.dataset['outfitSelect']) { outfitterSelected = btn.dataset['outfitSelect']; outfitterStatus = ''; outfitterNeedsRecovery = false; renderOutfitter(); return; }
    if (btn.dataset['quarry']) {
      commands.setTarget(btn.dataset['quarry']); chartStatus = `Target fish: ${btn.dataset['quarryLabel'] ?? btn.querySelector('.instrument-title')?.textContent ?? 'your fish'}. Study is now counting.`; dispatch({ kind: 'resume' }); return;
    }
    if (btn.dataset['destination']) {
      const result = commands.sailTo(btn.dataset['destination']);
      chartStatus = result.ok ? '' : result.reason === 'not-discovered'
        ? 'That destination is not charted yet. Your current course is unchanged.'
        : 'That destination is unavailable. Choose another place on this chart; your current course is unchanged.';
      if (result.ok) dispatch({ kind: 'resume' }); else render(); return;
    }
    if (btn.dataset['replay']) {
      commands.setTutorial(replayTutorial(btn.dataset['replay'] as TutorialStepId));
      if (state.origin === 'voyage') dispatch({ kind: 'resume' }); else render(); return;
    }
    switch (btn.id) {
      case 'goal-next': case 'pause-next': {
        const p = commands.presentation(); if (p === null) return;
        const next = voyageGuidance(p).action.kind;
        if (next === 'ending') { commands.finishAtlas(); open('ending', btn.id); }
        else if (next === 'collection' || next === 'studies') { guidePage = next; if (next === 'studies') studySpeciesId = null; open('guide', btn.id); guideContents.scrollTop = 0; }
        else { chartPage = next === 'quarry' ? 'quarry' : 'grounds'; open('chart', btn.id); }
        return;
      }
      case 'title-continue': start(commands.continueVoyage()); return;
      case 'title-sail':
        if (commands.hasValidSave() || commands.saveProblem() !== null) { sailConfirm = true; render(); document.getElementById('title-sail-yes')?.focus(); }
        else beginFisher('new', btn.id);
        return;
      case 'title-sail-yes': sailConfirm = false; beginFisher('new', 'title-sail', true); return;
      case 'title-sail-no': sailConfirm = false; render(); document.getElementById('title-sail')?.focus(); return;
      case 'title-help': guidePage = 'help'; open('guide', btn.id); return;
      case 'title-settings': open('settings', btn.id); return;
      case 'title-recovery': open('save', btn.id); return;
      case 'title-save': open('save', btn.id); return;
      case 'title-credits': open('credits', btn.id); return;
      case 'title-skip-tips': commands.setTutorial({ kind: 'skipped' }); render(); focusFirst(title); return;
      case 'ui-menu': open('pause', btn.id); return;
      case 'pause-fisher': beginFisher('edit', btn.id); return;
      case 'pause-outfitter': outfitterStatus = ''; outfitterNeedsRecovery = false; open('outfitter', btn.id); return;
      case 'fisher-save': saveFisher(); return;
      case 'fisher-recovery': open('save', btn.id); return;
      case 'fisher-cancel': case 'outfitter-back': dispatch({ kind: 'back' }); return;
      case 'outfitter-buy': changeOutfit('buy'); return;
      case 'outfitter-equip': changeOutfit('equip'); return;
      case 'outfitter-remove': changeOutfit('remove'); return;
      case 'outfitter-recovery': open('save', btn.id); return;
      case 'ui-harbour': case 'pause-harbour': open('harbour', btn.id); return;
      case 'harbour-back': dispatch({ kind: 'back' }); return;
      case 'ui-chart': chartPage = commands.presentation()?.profile.arrivedDestinations.length === 2 ? 'sea' : 'grounds'; open('chart', btn.id); return;
      case 'ui-guide': guidePage = 'collection'; open('guide', btn.id); return;
      case 'chart-grounds': chartPage = 'grounds'; render(); goalBody.scrollTop = 0; return;
      case 'chart-quarry': chartPage = 'quarry'; render(); goalBody.scrollTop = 0; return;
      case 'chart-sea': chartPage = 'sea'; render(); goalBody.scrollTop = 0; return;
      case 'chart-natural': case 'pause-natural': commands.returnToNaturalCourse(); chartStatus = ''; dispatch({ kind: 'resume' }); return;
      case 'guide-help': case 'guide-collection': case 'guide-studies': case 'guide-voyage': case 'guide-letters': guidePage = btn.id.slice(6) as typeof guidePage; render(); guideContents.scrollTop = 0; guideHelp.scrollTop = 0; return;
      case 'goal-ending': case 'guide-ending': commands.finishAtlas(); open('ending', btn.id); return;
      case 'ending-credits': open('credits', btn.id); return;
      case 'ending-continue': commands.continueFishing(); dispatch({ kind: 'resume' }); return;
      case 'offline-back': commands.dismissOfflineReturn(); dispatch({ kind: 'back' }); return;
      case 'offline-chart': commands.dismissOfflineReturn(); dispatch({ kind: 'back' }); chartPage = 'grounds'; open('chart', 'ui-chart'); return;
      case 'pause-skip-tips': case 'tip-skip-all': commands.setTutorial({ kind: 'skipped' }); render(); return;
      case 'tip-skip': commands.skipTip(); render(); return;
      case 'tip-watch': commands.setTutorial({ kind: 'active', stepId: 'first-catch' }); render(); return;
      case 'tip-away': {
        let mode = advanceTutorial(commands.tutorial(), { kind: 'away' });
        if (commands.presentation()?.phase === 'moored') mode = advanceTutorial(mode, { kind: 'arrival' });
        commands.setTutorial(mode); render(); return;
      }
      case 'tip-quarry': {
        const mode = commands.tutorial();
        if ((mode.kind === 'active' || mode.kind === 'replay') && mode.stepId === 'first-catch') commands.setTutorial({ kind: mode.kind, stepId: 'choose-quarry' });
        chartPage = 'quarry'; open('chart', 'ui-chart'); return;
      }
      case 'tip-chart': chartPage = 'grounds'; open('chart', 'ui-chart'); return;
      case 'pause-resume': dispatch({ kind: 'resume' }); return;
      case 'pause-goal': open('chart', btn.id); return;
      case 'pause-guide': guidePage = 'voyage'; open('guide', btn.id); return;
      case 'pause-settings': open('settings', btn.id); return;
      case 'pause-save': open('save', btn.id); return;
      case 'pause-title': commands.returnToTitle(); dispatch({ kind: 'return-to-title' }); return;
      case 'goal-back': case 'guide-back': case 'settings-done': case 'credits-back': case 'save-back':
        dispatch({ kind: 'back' }); return;
      case 'set-replay-tips': commands.setTutorial(replayTutorial('watch-autonomy')); if (state.origin === 'voyage') dispatch({ kind: 'resume' }); else render(); return;
      case 'set-reset-tips': commands.setTutorial({ kind: 'unseen' }); render(); return;
      case 'save-export':
        saveText.value = commands.exportSave();
        saveStatus = 'The save is in the box. Copy it somewhere safe.';
        render(); saveText.focus(); saveText.select();
        return;
      case 'save-export-backup': saveText.value = commands.exportBackup() ?? ''; saveStatus = 'Original backup bytes are in the box. Copy them somewhere safe.'; render(); saveText.focus(); saveText.select(); return;
      case 'save-download-backup': {
        const text = commands.exportBackup();
        if (text === null) { saveStatus = 'Backup bytes could not be read. The current voyage is untouched.'; render(); return; }
        const url = URL.createObjectURL(new Blob([text], { type: 'text/plain' })); h('a', { href: url, download: 'reelin-backup-original.txt' }).click(); URL.revokeObjectURL(url);
        saveStatus = 'Original backup download prepared.'; render(); return;
      }
      case 'save-copy': {
        saveText.value = commands.exportSave();
        if (!navigator.clipboard?.writeText) { saveStatus = 'The save is in the box. Select and copy the text.'; render(); saveText.focus(); saveText.select(); return; }
        void navigator.clipboard.writeText(saveText.value).then(() => { saveStatus = 'Save copied.'; render(); }, () => { saveStatus = 'Clipboard access was unavailable. Copy the selected save text.'; render(); saveText.focus(); saveText.select(); });
        return;
      }
      case 'save-download': {
        const blob = new Blob([commands.exportSave()], { type: 'text/plain' }); const url = URL.createObjectURL(blob);
        const link = h('a', { href: url, download: 'reelin-voyage.txt' }); link.click(); URL.revokeObjectURL(url);
        saveStatus = 'Save download prepared.'; render(); return;
      }
      case 'save-import': {
        const text = saveText.value.trim();
        if (text === '') { saveStatus = 'Paste a save into the box first.'; render(); return; }
        saveStatus = importWords(commands.importSave(text));
        render();
        return;
      }
      case 'save-restore': saveStatus = importWords(commands.restoreBackup()); render(); return;
      case 'save-new': newConfirm = true; render(); document.getElementById('save-new-yes')?.focus(); return;
      case 'save-new-no': newConfirm = false; render(); document.getElementById('save-new')?.focus(); return;
      case 'save-new-yes': {
        newConfirm = false;
        const r = commands.startNew();
        saveStatus = r.ok ? 'A new voyage has begun. The old one is the backup.' : commands.saveFailure() !== null ? 'A save write failed. Recovery retains the backup. Export the current in-memory voyage before closing.' : `Not started: ${reasonWords(r.reason)}. The current voyage is untouched.`;
        render(); document.getElementById('save-new')?.focus();
        return;
      }
      case 'save-reload': commands.reload(); return;
      // The plate's Confirm. Presentation only, and the plate redraws from the host's answer.
      case 'catch-reel': commands.reel(); renderCatch(); return;
      default: return;
    }
  });

  const onSetting = (patch: () => Partial<PlayerSettings>) => () => {
    commands.updateSettings(patch()); renderSettings();
    if (commands.saveFailure() !== null) { fatalText = 'The changed settings could not be saved on this device.'; dispatch({ kind: 'fatal' }); }
  };
  fisherName.addEventListener('input', () => { fisherDraft = { ...fisherDraft, name: fisherName.value }; fisherStatus = ''; fisherStatusEl.textContent = ''; });
  fisherName.addEventListener('keydown', event => { if (event.key === 'Enter') { event.preventDefault(); event.stopPropagation(); saveFisher(); } });
  saveFile.addEventListener('change', () => {
    const file = saveFile.files?.[0]; if (file === undefined) return;
    void file.text().then(text => { saveText.value = text; saveStatus = importWords(commands.importSave(text)); saveFile.value = ''; render(); }, () => { saveStatus = 'The file could not be read. Paste the save text instead.'; render(); });
  });
  const level = (input: HTMLInputElement): number => Math.min(1, Math.max(0, Number(input.value) / 100));
  setMaster.addEventListener('input', onSetting(() => ({ masterVolume: level(setMaster) })));
  setSea.addEventListener('input', onSetting(() => ({ seaVolume: level(setSea) })));
  setEffects.addEventListener('input', onSetting(() => ({ effectsVolume: level(setEffects) })));
  setMuted.addEventListener('change', onSetting(() => ({ muted: setMuted.checked })));
  setMotion.addEventListener('change', onSetting(() => ({ reducedMotion: setMotion.checked })));
  const onMotionQuery = (): void => { renderSettings(); };
  motionQuery?.addEventListener('change', onMotionQuery);
  setContrast.addEventListener('change', onSetting(() => ({ highContrastRoute: setContrast.checked })));
  setScale1.addEventListener('change', onSetting(() => ({ uiScale: 1 })));
  setScale2.addEventListener('change', onSetting(() => ({ uiScale: 2 })));
  setText.addEventListener('change', onSetting(() => ({ textScale: setText.value === '1.5' ? 1.5 : setText.value === '1.25' ? 1.25 : 1 })));
  setReading.addEventListener('change', onSetting(() => ({ readingMode: setReading.checked })));
  setReveal.addEventListener('change', onSetting(() => ({ catchReveal: setReveal.value === 'notable' ? 'notable' : setReveal.value === 'new-only' ? 'new-only' : 'all' })));
  setRelaxed.addEventListener('change', onSetting(() => ({ relaxedFishing: setRelaxed.checked })));
  setGlyphs.addEventListener('change', onSetting(() => ({ glyphs: setGlyphs.value === 'keyboard' ? 'keyboard' : setGlyphs.value === 'gamepad' ? 'gamepad' : 'auto' })));

  function onKey(e: KeyboardEvent): void {
    if (sheetOpen(state)) {
      const top = topSheet(state);
      const scope = state.mode === 'fatal-recovery' ? save.sheet : top === null ? null : sheetOf[top.id];
      if (scope === null || scope === undefined) return;
      if (e.key === 'Escape' && state.mode !== 'fatal-recovery') {
        e.preventDefault(); e.stopPropagation();
        if (newConfirm && top?.id === 'save') { newConfirm = false; render(); document.getElementById('save-new')?.focus(); return; }
        if (top?.id === 'offline') commands.dismissOfflineReturn();
        if (top?.id === 'ending') commands.continueFishing();
        dispatch({ kind: 'back' });
        return;
      }
      if (e.key === 'Tab') {
        const list = focusables(scope);
        if (list.length === 0) return;
        const first = list[0] as HTMLElement;
        const last = list[list.length - 1] as HTMLElement;
        const current = document.activeElement as HTMLElement | null;
        if (current === null || !scope.contains(current)) { e.preventDefault(); first.focus(); return; }
        if (!list.includes(current)) { e.preventDefault(); (e.shiftKey ? last : first).focus(); return; }
        if (!e.shiftKey && current === last) { e.preventDefault(); first.focus(); }
        else if (e.shiftKey && current === first) { e.preventDefault(); last.focus(); }
      }
      return;
    }
    if (state.mode === 'title' && e.key === 'Escape' && sailConfirm) {
      sailConfirm = false; render(); document.getElementById('title-sail')?.focus(); e.stopPropagation();
      return;
    }
    // THE TICKET'S OWN SHORTCUT, MADE TRUE. `renderTitle` prints the confirm glyph beside the
    // stamped row - Continue Voyage, or Set Sail on a first start - but the title deliberately
    // takes no focus at boot (`focusForTransition`: a page that grabs focus at load rearranges
    // the tab order the platform instruments measure). So there was nothing for the platform to
    // activate, Enter did nothing at all, and the ticket sat over the picture for ever while the
    // voyage the player had asked for never began. Only the stamped row answers, which is the
    // one row the ticket ever prints the glyph on; a key that already has a control to act on is
    // left to the platform, so this can never fire an activation twice.
    if (state.mode === 'title' && e.key === 'Enter' && !activates(document.activeElement)) {
      const stamped = titleRows.querySelector<HTMLButtonElement>('button.row.stamp');
      if (stamped !== null) { e.preventDefault(); stamped.click(); }
      return;
    }
    if (state.mode === 'voyage' && e.key === 'Escape' && !commands.ledgerOpen()) {
      e.preventDefault(); e.stopPropagation();
      open('pause', 'ui-menu');
    }
  }
  document.addEventListener('keydown', onKey);
  function onPointerType(e: PointerEvent): void { lastPointerType = e.pointerType; }
  document.addEventListener('pointerdown', onPointerType, { capture: true, passive: true });
  // Focus that escapes a modal sheet (a click on the inert picture in an older engine) comes back.
  function onFocusIn(e: FocusEvent): void {
    if (!sheetOpen(state)) return;
    const top = topSheet(state);
    const scope = state.mode === 'fatal-recovery' ? save.sheet : top === null ? null : sheetOf[top.id];
    if (scope && !scope.contains(e.target as Node)) focusFirst(scope);
  }
  document.addEventListener('focusin', onFocusIn);

  render();

  return {
    root,
    state: () => state,
    dispatch,
    refresh: render,
    refreshCatch(camera?: PlateCamera): void {
      const front = topSheet(state)?.id;
      const transactionRecoveryVisible = front === 'fisher' && fisherNeedsRecovery || front === 'outfitter' && outfitterNeedsRecovery;
      if (commands.saveFailure() !== null && state.origin === 'voyage' && state.mode !== 'fatal-recovery' && !transactionRecoveryVisible) {
        fatalText = 'Changes could not be saved on this device. Export the current voyage before closing.';
        dispatch({ kind: 'fatal' }); return;
      }
      if (camera !== undefined) plateCamera = camera;
      renderCatch();
      renderInstruments();
      refreshCourse();
    },
    catchPlateAnchor(): { readonly x: number; readonly y: number } | null {
      if (plate.hidden) return null;
      return { x: plate.offsetLeft + plate.offsetWidth / 2, y: plate.offsetTop };
    },
    showFatal(message: string): void {
      fatalText = message.trim().replace(/\s+/g, ' ');
      if (!/[.!?]$/.test(fatalText)) fatalText += '.';
      saveStatus = '';
      dispatch({ kind: 'fatal' });
      render();
    },
    subscribe(listener) { listeners.add(listener); return () => { listeners.delete(listener); }; },
    destroy(): void {
      document.removeEventListener('keydown', onKey);
      document.removeEventListener('pointerdown', onPointerType, { capture: true });
      document.removeEventListener('focusin', onFocusIn);
      motionQuery?.removeEventListener('change', onMotionQuery);
      goalResize?.disconnect();
      harbourPanel.destroy();
      listeners.clear();
      clear(root);
    },
  };
}
